import { useEffect, useState, useCallback, useRef, useMemo, createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import storage from "../firebase.js";
import { useAuth } from "../contexts/AuthContext";
import professionServiceData from "../professionServiceData.js";

const BASE_URL = "https://us-central1-workexpress-1ffff.cloudfunctions.net/app";

const normalizeCorrectionFieldList = (raw) => {
  if (!Array.isArray(raw)) return [];
  return [...new Set(
    raw
      .map((x) => {
        if (typeof x === "string") return x.trim();
        if (x && typeof x === "object" && x.key != null) return String(x.key).trim();
        return "";
      })
      .filter((s) => s !== "")
  )].sort();
};

/** API/Firestore may store profession as a string or as { label, value }. */
const normalizeProfessionValue = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  if (Array.isArray(value)) return normalizeProfessionValue(value[0]);
  if (typeof value === "object") {
    const candidate = value.value ?? value.label ?? value.name ?? value.profession;
    if (candidate != null) return normalizeProfessionValue(candidate);
    return "";
  }
  return "";
};

const displayTextValue = (value, fallback = "N/A") => {
  if (value == null || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    const text = String(value).trim();
    return text || fallback;
  }
  if (Array.isArray(value)) {
    const text = value.map((item) => displayTextValue(item, "")).filter(Boolean).join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate =
      value.label ??
      value.name ??
      value.title ??
      value.value ??
      value.profession ??
      value.service ??
      value.subService ??
      value.type;
    if (candidate != null) return displayTextValue(candidate, fallback);
  }
  return fallback;
};

const normalizeTextArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => displayTextValue(item, "")).filter(Boolean);
};

function normalizeServicemanProfessionFields(data) {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };
  if (next.profession != null) {
    next.profession = normalizeProfessionValue(next.profession) || next.profession;
  }
  if (next.secondaryProfession != null) {
    next.secondaryProfession = normalizeProfessionValue(next.secondaryProfession);
  }
  if (Array.isArray(next.professions)) {
    next.professions = next.professions.map((item) => normalizeProfessionValue(item) || displayTextValue(item, ""));
  }
  return next;
}

/** Fields that must not show “mark for correction” (system / computed / locked-in UI). */
const CORRECTION_TOGGLE_EXCLUDED_KEYS = new Set([
  "verified",
  "phone",
  "dateOfBirth",
  "deviceType",
  "lastSeen",
  "amountPaid",
  "havePaid",
  "uid",
  "referralCode",
  "couponCode",
  "couponDiscount",
  "profileComplete",
  "createdAt",
  "isOnline",
  "bookingsCount",
  "callNowCount",
  "impressions",
  "mpin",
  "profession",
  "professions",
  "availabilityStart",
  "availabilityEnd",
  "services",
  "subServices",
  "avgRating",
  "reviewCount",
  "experienceRange",
  "teamSize",
  "gender",
  "brandExpertise",
  "engineCC",
  "fuelType",
  "workshopType",
  "secondaryProfession",
  "professionsSecondary",
  "enableSecondaryProfession",
  "secondaryAreaName",
  "secondaryAvailabilityStart",
  "secondaryAvailabilityEnd",
  "secondaryServices",
  "secondarySubServices",
  "secondaryExperienceRange",
  "secondaryTeamSize",
  "secondaryMinimalVisitCharge",
  "secondaryBrandExpertise",
  "secondaryEngineCC",
  "secondaryFuelType",
  "secondaryWorkshopType",
  "callActionRecords",
]);

function isCorrectionKeyExcluded(key) {
  return typeof key === "string" && CORRECTION_TOGGLE_EXCLUDED_KEYS.has(key);
}

function isFieldMarkedForCorrection(correctionFields, key) {
  if (!Array.isArray(correctionFields) || !key || isCorrectionKeyExcluded(key)) return false;
  return correctionFields.includes(key);
}

function filterExcludedCorrectionKeys(raw) {
  return normalizeCorrectionFieldList(raw).filter((k) => !isCorrectionKeyExcluded(k));
}

/** Convert a serviceman field value to something JSON/Firestore-friendly for correction snapshots. */
function correctionValueToStorable(value) {
  if (value === undefined) return null;
  if (value === null) return null;
  if (typeof value === "number" && Number.isNaN(value)) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "bigint") return String(value);
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (value && typeof value.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
    } catch {
      return null;
    }
  }
  if (typeof value === "object" && typeof value._seconds === "number") {
    const nanos = typeof value._nanoseconds === "number" ? value._nanoseconds : 0;
    return new Date(value._seconds * 1000 + nanos / 1e6).toISOString();
  }
  try {
    return JSON.parse(
      JSON.stringify(value, (_k, v) => {
        if (v === undefined) return null;
        if (v && typeof v.toDate === "function") {
          try {
            const d = v.toDate();
            return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
          } catch {
            return null;
          }
        }
        if (typeof v === "object" && v !== null && typeof v._seconds === "number") {
          const n = typeof v._nanoseconds === "number" ? v._nanoseconds : 0;
          return new Date(v._seconds * 1000 + n / 1e6).toISOString();
        }
        return v;
      })
    );
  } catch {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
}

/** Build `{ [fieldKey]: snapshot }` for every key in `fieldKeys` from current serviceman (+ stats for callActionRecords). */
function buildCorrectionFieldValuesPayload(serviceman, fieldKeys, statsRows) {
  const out = {};
  const keys = Array.isArray(fieldKeys) ? fieldKeys : [];
  for (const key of keys) {
    if (key === "callActionRecords") {
      const rows = Array.isArray(statsRows) ? statsRows : [];
      out[key] = correctionValueToStorable(
        rows.map((s) => ({
          id: s.id ?? null,
          timestamp: s.timestamp,
          type: s.type ?? null,
          userName: s.userName ?? null,
          userId: s.userId ?? null,
        }))
      );
      continue;
    }
    if (!serviceman || typeof key !== "string") {
      out[key] = null;
      continue;
    }
    const raw = Object.prototype.hasOwnProperty.call(serviceman, key) ? serviceman[key] : undefined;
    out[key] = correctionValueToStorable(raw);
  }
  return out;
}

function formatCorrectionPreviewValue(value, maxLen = 480) {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") {
    return value.length > maxLen ? `${value.slice(0, maxLen)}…` : value;
  }
  try {
    const s = typeof value === "object" ? JSON.stringify(value) : String(value);
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
  } catch {
    return String(value);
  }
}

const CORRECTION_URL_IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i;

function correctionLooksLikeHttpUrl(s) {
  return typeof s === "string" && /^https?:\/\//i.test(s.trim());
}

function correctionIsLikelyImageUrl(url) {
  if (!correctionLooksLikeHttpUrl(url)) return false;
  if (CORRECTION_URL_IMAGE_EXT.test(url)) return true;
  try {
    return CORRECTION_URL_IMAGE_EXT.test(decodeURIComponent(url).toLowerCase());
  } catch {
    return false;
  }
}

function correctionLabelForFileUrl(url) {
  if (!correctionLooksLikeHttpUrl(url)) return "Open file";
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const base = path.split("/").filter(Boolean).pop() || "file";
    return base.length > 56 ? `${base.slice(0, 53)}…` : base;
  } catch {
    const tail = (url.split("/").pop() || "Open file").replace(/\?.*$/, "");
    return tail.length > 56 ? `${tail.slice(0, 53)}…` : tail;
  }
}

/** Pick a displayable storage URL from snapshot shapes (string, aadhar, document, media item). */
function correctionExtractUrl(value) {
  if (correctionLooksLikeHttpUrl(value)) return value.trim();
  if (!value || typeof value !== "object") return "";
  if (correctionLooksLikeHttpUrl(value.url)) return value.url.trim();
  const nested = value.file;
  if (nested && typeof nested === "object" && correctionLooksLikeHttpUrl(nested.url)) return nested.url.trim();
  return "";
}

function CorrectionImageWithLink({ url, alt, treatAsImage }) {
  const [imgFailed, setImgFailed] = useState(false);
  const tryImg = treatAsImage && !imgFailed;
  if (!tryImg) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="correction-rich-file-link">
        {alt || correctionLabelForFileUrl(url)}
      </a>
    );
  }
  return (
    <div className="correction-rich-media-block">
      <img src={url} alt="" className="correction-rich-img" onError={() => setImgFailed(true)} />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="correction-rich-file-link correction-rich-file-link--below"
      >
        {alt || "Open in new tab"}
      </a>
    </div>
  );
}

/**
 * Renders correction snapshot / preview values: image URLs as thumbnails, other URLs as anchors (target blank), not raw links.
 */
function CorrectionRichValue({ value, fieldKey }) {
  if (value === undefined || value === null) {
    return <span className="correction-rich-empty">—</span>;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return <span className="correction-rich-text">{String(value)}</span>;
  }
  if (typeof value === "string") {
    if (correctionLooksLikeHttpUrl(value)) {
      const treatAsImage = fieldKey === "image" || correctionIsLikelyImageUrl(value);
      return <CorrectionImageWithLink url={value.trim()} alt={correctionLabelForFileUrl(value)} treatAsImage={treatAsImage} />;
    }
    return <span className="correction-rich-text">{formatCorrectionPreviewValue(value, 8000)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="correction-rich-empty">—</span>;
    return (
      <ul className="correction-rich-list">
        {value.map((item, i) => (
          <li key={i}>
            <CorrectionRichValue value={item} fieldKey={fieldKey} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === "object") {
    const url = correctionExtractUrl(value);
    const mediaType = typeof value.type === "string" ? value.type.toLowerCase() : "";
    const linkLabel =
      value.name ||
      value.title ||
      value.file?.name ||
      value.file?.fileName ||
      (url ? correctionLabelForFileUrl(url) : null);

    if (url && correctionLooksLikeHttpUrl(url)) {
      if (mediaType === "video") {
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" className="correction-rich-file-link">
            {linkLabel || "Open video"}
          </a>
        );
      }
      const treatAsImage =
        fieldKey === "image" ||
        fieldKey === "aadharCard" ||
        mediaType === "image" ||
        correctionIsLikelyImageUrl(url);
      return <CorrectionImageWithLink url={url} alt={linkLabel || undefined} treatAsImage={treatAsImage} />;
    }

    return (
      <pre className="correction-value-pre correction-value-pre--nested">{formatCorrectionPreviewValue(value, 4000)}</pre>
    );
  }
  return <span className="correction-rich-text">{formatCorrectionPreviewValue(value, 8000)}</span>;
}

/** True if this field has a stored snapshot in `correctionFieldValues` (source of "Previous"). */
function hasCorrectionFieldSnapshot(serviceman, key) {
  if (!serviceman || !key || isCorrectionKeyExcluded(key)) return false;
  const m = serviceman.correctionFieldValues;
  if (!m || typeof m !== "object" || Array.isArray(m)) return false;
  return Object.prototype.hasOwnProperty.call(m, key);
}

function correctionComparableHash(value) {
  try {
    return JSON.stringify(correctionValueToStorable(value));
  } catch {
    return String(value);
  }
}

/** Highlight only while a field is actively listed for correction (not snapshot-only after fixes). */
function isCorrectionCardHighlighted(correctionFields, serviceman, key) {
  return isFieldMarkedForCorrection(correctionFields, key);
}

/**
 * After a correction-only PUT, Firestore omits deleted fields from JSON — merge would otherwise
 * keep stale `correctionFields` / `correctionFieldValues` on client state.
 */
function mergeServicemanFromPutResponse(prev, body) {
  if (!body || typeof body !== "object") return prev;
  const id = body.id || prev?.id;
  const merged = { ...prev, ...body, id };
  if (!Object.prototype.hasOwnProperty.call(body, "correctionFields")) {
    delete merged.correctionFields;
  }
  if (!Object.prototype.hasOwnProperty.call(body, "correctionFieldValues")) {
    delete merged.correctionFieldValues;
  }
  return merged;
}

const ServicemanCorrectionDisplayContext = createContext(null);

function CorrectionFieldDualCompare({ fieldKey, children }) {
  const ctx = useContext(ServicemanCorrectionDisplayContext);
  const serviceman = ctx?.serviceman;
  if (!serviceman || !fieldKey || !hasCorrectionFieldSnapshot(serviceman, fieldKey)) {
    return <>{children}</>;
  }
  const map = serviceman.correctionFieldValues;
  const rawSnap = map[fieldKey];
  const currentRaw = Object.prototype.hasOwnProperty.call(serviceman, fieldKey)
    ? serviceman[fieldKey]
    : undefined;
  const snapshotMatchesCurrent =
    correctionComparableHash(rawSnap) === correctionComparableHash(currentRaw);
  // Show Previous/Current only after partner actually changes the value.
  if (snapshotMatchesCurrent) {
    return <>{children}</>;
  }
  const sideBySide = fieldKey === "image";
  return (
    <div className={`correction-dual-wrap${sideBySide ? " correction-dual-wrap--profile-pic" : ""}`}>
      <div className="correction-value-old">
        <span className="correction-value-label">Previous</span>
        <div className="correction-value-rich">
          <CorrectionRichValue value={rawSnap} fieldKey={fieldKey} />
        </div>
      </div>
      <div className="correction-value-new">
        <span className="correction-value-label">Current</span>
        <div className="correction-value-new-body">{children}</div>
      </div>
    </div>
  );
}

function ProfileCorrectionToggle({ active, disabled, onToggle, label }) {
  return (
    <button
      type="button"
      className={`profile-correction-toggle${active ? " profile-correction-toggle--active" : ""}`}
      aria-label={active ? `${label}: marked for correction` : `${label}: mark for correction`}
      aria-pressed={active}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      {active ? "✓" : ""}
    </button>
  );
}

function BentoProfileField({ fieldKey, label, wide, valueClassName, correctionFields, canEdit, toggleField, children }) {
  const excluded = isCorrectionKeyExcluded(fieldKey);
  const active = isFieldMarkedForCorrection(correctionFields, fieldKey);
  const cardMarked = active;
  return (
    <article className={`profile-bento-card${wide ? " profile-bento-card--wide" : ""}${cardMarked ? " is-marked" : ""}`.trim()}>
      <div className="profile-bento-card-head">
        <span className="profile-bento-label">{label}</span>
        {canEdit && !excluded && (
          <ProfileCorrectionToggle
            active={active}
            disabled={!canEdit}
            onToggle={() => toggleField(fieldKey)}
            label={label}
          />
        )}
      </div>
      <div className={valueClassName ? `profile-bento-value ${valueClassName}` : "profile-bento-value"}>
        <CorrectionFieldDualCompare fieldKey={fieldKey}>{children}</CorrectionFieldDualCompare>
      </div>
    </article>
  );
}

function ProfileSubsectionWithCorrection({ title, fieldKey, canEdit, correctionFields, toggleField, children }) {
  const excluded = isCorrectionKeyExcluded(fieldKey);
  const active = isFieldMarkedForCorrection(correctionFields, fieldKey);
  const sectionMarked = active;
  return (
    <div
      className={`profile-container profile-subsection-correction${sectionMarked ? " is-marked-section" : ""}`.trim()}
      style={{ marginTop: "1.5rem" }}
    >
      <div className="profile-subsection-correction-head">
        <h4 className="subsection-title profile-subsection-correction-title">{title}</h4>
        {canEdit && !excluded && (
          <ProfileCorrectionToggle
            active={active}
            disabled={!canEdit}
            onToggle={() => toggleField(fieldKey)}
            label={title}
          />
        )}
      </div>
      <CorrectionFieldDualCompare fieldKey={fieldKey}>{children}</CorrectionFieldDualCompare>
    </div>
  );
}

const ServicemanProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { serviceman: initialServiceman } = location.state || {};
  const [serviceman, setServiceman] = useState(() =>
    initialServiceman ? normalizeServicemanProfessionFields(initialServiceman) : initialServiceman
  );
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [media, setMedia] = useState(initialServiceman?.media || []);
  const [newMediaFiles, setNewMediaFiles] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(-1);
  const [selectedMediaType, setSelectedMediaType] = useState('primary'); // 'primary' or 'secondary'
  const [servicemenStats, setServicemenStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [showProfilePictureFullscreen, setShowProfilePictureFullscreen] = useState(false);
  const [hiddenMedia, setHiddenMedia] = useState(initialServiceman?.hidden || []);
  const [hiddenDocuments, setHiddenDocuments] = useState(initialServiceman?.hiddenDocuments || []);
  const [hiddenSecondaryMedia, setHiddenSecondaryMedia] = useState(initialServiceman?.hiddenSecondary || []);
  const [hiddenSecondaryDocuments, setHiddenSecondaryDocuments] = useState(initialServiceman?.hiddenSecondaryDocuments || []);
  const [secondaryMedia, setSecondaryMedia] = useState(initialServiceman?.secondaryMedia || []);
  const [newSecondaryMediaFiles, setNewSecondaryMediaFiles] = useState([]);
  const [documents, setDocuments] = useState(initialServiceman?.documents || []);
  const [newDocumentFiles, setNewDocumentFiles] = useState([]);
  const [secondaryDocuments, setSecondaryDocuments] = useState(initialServiceman?.secondaryDocuments || []);
  const [newSecondaryDocumentFiles, setNewSecondaryDocumentFiles] = useState([]);
  const [deletedMediaUrls, setDeletedMediaUrls] = useState([]);
  const [deletedDocumentUrls, setDeletedDocumentUrls] = useState([]);
  const [deletedSecondaryMediaUrls, setDeletedSecondaryMediaUrls] = useState([]);
  const [deletedSecondaryDocumentUrls, setDeletedSecondaryDocumentUrls] = useState([]);
  const [isProfilePictureDeleted, setIsProfilePictureDeleted] = useState(false);
  const [areaNames, setAreaNames] = useState([]);
  const [loadingAreaNames, setLoadingAreaNames] = useState(false);
  const DEFAULT_IMAGE = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";
  const POLLING_INTERVAL = 5000;

  const servicesContainerRef = useRef(null);

  // Check if user is admin (super_admin)
  const isAdmin = user && user.role === "super_admin";
  // Only admin and manager can edit servicemen details
  const canEditServiceman =
    user && (user.role === "super_admin" || user.role === "manager");

  const [correctionFields, setCorrectionFields] = useState(() =>
    filterExcludedCorrectionKeys(initialServiceman?.correctionFields)
  );
  const [hasUnsavedCorrectionChanges, setHasUnsavedCorrectionChanges] = useState(false);
  const [savingCorrections, setSavingCorrections] = useState(false);
  const [correctionHoverOpen, setCorrectionHoverOpen] = useState(false);
  const correctionHoverLeaveTimer = useRef(null);

  // Sync from server only when user has no pending local correction edits.
  useEffect(() => {
    if (!serviceman?.id) return;
    if (hasUnsavedCorrectionChanges) return;
    setCorrectionFields(filterExcludedCorrectionKeys(serviceman.correctionFields));
  }, [serviceman?.id, serviceman?.correctionFields, hasUnsavedCorrectionChanges]);

  useEffect(() => {
    return () => {
      if (correctionHoverLeaveTimer.current) {
        clearTimeout(correctionHoverLeaveTimer.current);
      }
    };
  }, []);

  const openCorrectionPreview = useCallback(() => {
    if (correctionHoverLeaveTimer.current) {
      clearTimeout(correctionHoverLeaveTimer.current);
      correctionHoverLeaveTimer.current = null;
    }
    setCorrectionHoverOpen(true);
  }, []);

  const scheduleCloseCorrectionPreview = useCallback(() => {
    if (correctionHoverLeaveTimer.current) {
      clearTimeout(correctionHoverLeaveTimer.current);
    }
    correctionHoverLeaveTimer.current = setTimeout(() => {
      setCorrectionHoverOpen(false);
      correctionHoverLeaveTimer.current = null;
    }, 240);
  }, []);

  const correctionPreviewEntries = useMemo(() => {
    const currentForKey = (k) => {
      if (k === "callActionRecords") {
        return correctionValueToStorable(
          (servicemenStats || []).map((s) => ({
            id: s.id ?? null,
            timestamp: s.timestamp,
            type: s.type ?? null,
            userName: s.userName ?? null,
            userId: s.userId ?? null,
          }))
        );
      }
      return correctionValueToStorable(serviceman?.[k]);
    };

    const pending = filterExcludedCorrectionKeys(correctionFields);
    if (pending.length === 0) return [];
    return pending.map((k) => ({
      key: k,
      current: currentForKey(k),
    }));
  }, [correctionFields, serviceman, servicemenStats]);

  const hasMarkedCorrections = correctionFields.length > 0;

  const toggleCorrectionField = useCallback(
    (key) => {
      if (!canEditServiceman || !key || isCorrectionKeyExcluded(key)) return;
      setHasUnsavedCorrectionChanges(true);
      setCorrectionFields((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return Array.from(next).sort();
      });
    },
    [canEditServiceman]
  );

  const handleSaveCorrections = useCallback(async () => {
    if (!serviceman?.id || !canEditServiceman) return;
    /** Checkbox selection is the source of truth (includes clearing all marks → []). */
    const finalKeys = filterExcludedCorrectionKeys(correctionFields);

    setSavingCorrections(true);
    try {
      const correctionFieldValues = buildCorrectionFieldValuesPayload(serviceman, finalKeys, servicemenStats);
      const res = await fetch(`${BASE_URL}/servicemen/${serviceman.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ correctionFields: finalKeys, correctionFieldValues }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to save (${res.status})`);
      }
      const updated = await res.json();
      setServiceman((prev) =>
        normalizeServicemanProfessionFields(
          mergeServicemanFromPutResponse(prev, { ...updated, id: updated.id || prev?.id })
        )
      );
      setCorrectionFields(finalKeys);
      setHasUnsavedCorrectionChanges(false);
      setCorrectionHoverOpen(false);
      alert("Correction list updated on this serviceman document.");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save correction fields");
    } finally {
      setSavingCorrections(false);
    }
  }, [serviceman, canEditServiceman, correctionFields, servicemenStats]);

  /** Same as unchecking the field: drop key from the list to be saved. */
  const removeCorrectionPreviewRow = useCallback((key) => {
    if (!key || !canEditServiceman) return;
    setHasUnsavedCorrectionChanges(true);
    setCorrectionFields((prev) => prev.filter((k) => k !== key));
  }, [canEditServiceman]);

  // Get profession options from professionServiceData
  const professionOptions = professionServiceData.map(item => ({
    label: item.profession.label,
    value: item.profession.value
  }));

  // Helper function to get profession data by value
  const getProfessionData = (professionValue) => {
    return professionServiceData.find(item =>
      item.profession.value === professionValue ||
      item.profession.label === professionValue
    );
  };

  // Helper function to get services for a profession
  const getServicesForProfession = (professionValue) => {
    const professionData = getProfessionData(professionValue);
    if (!professionData) return [];
    return professionData.services || [];
  };

  // Helper function to get sub-services for a service
  const getSubServicesForService = (professionValue, serviceValue) => {
    const professionData = getProfessionData(professionValue);
    if (!professionData || !professionData.services) return [];
    const service = professionData.services.find(s =>
      s.value === serviceValue || s.label === serviceValue
    );
    return service?.subServices || [];
  };

  // Helper function to check if profession has vehicle-related fields
  const hasVehicleFields = (professionValue) => {
    const professionData = getProfessionData(professionValue);
    if (!professionData) return false;
    return !!(professionData.engineCC || professionData.brandExpertise ||
      professionData.workshopType || professionData.fuelType ||
      professionData.vehicleTypes);
  };

  // Get vehicle-related fields for a profession
  const getVehicleFields = (professionValue) => {
    const professionData = getProfessionData(professionValue);
    if (!professionData) return null;
    return {
      engineCC: professionData.engineCC || [],
      brandExpertise: professionData.brandExpertise || [],
      workshopType: professionData.workshopType || [],
      fuelType: professionData.fuelType || [],
      vehicleTypes: professionData.vehicleTypes || null,
    };
  };

  // Helper function to convert Firestore timestamp to HH:MM format for time input
  const timestampToTimeString = (timestamp) => {
    if (!timestamp) return "";

    let date;

    // Handle Firestore timestamp with _seconds
    if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    }
    // Handle Firestore Timestamp object with toDate method
    else if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    }
    // Handle Date object
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle string
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    // Handle number (milliseconds)
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    else {
      return "";
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }

    // Format as HH:MM
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  /** App may store verification as `isVerified` (admin) or `verified` (mobile). */
  const getServicemanVerified = (sm) => {
    if (!sm || typeof sm !== "object") return false;
    if (sm.isVerified === true || sm.verified === true) return true;
    if (sm.isVerified === false && sm.verified !== true) return false;
    if (sm.verified === false && sm.isVerified !== true) return false;
    return !!(sm.isVerified || sm.verified);
  };

  const TEAM_SIZE_LABELS = {
    one: "1 person",
    one_two: "1–2 people",
    three_four: "3–4 people",
    five_plus: "5+ people",
    five_ten: "5–10 people",
  };

  const formatTeamSizeDisplay = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    const key = String(value).trim().toLowerCase();
    return TEAM_SIZE_LABELS[key] || String(value).replace(/_/g, " ");
  };

  const renderStorageFileLink = (file, fallbackLabel) => {
    if (!file || typeof file !== "object" || !file.url) return "N/A";
    const label = file.name || fallbackLabel;
    return (
      <CorrectionImageWithLink
        url={file.url}
        alt={label}
        treatAsImage={correctionIsLikelyImageUrl(file.url)}
      />
    );
  };

  const formatFullServicePackage = (pkg) => {
    if (!pkg || typeof pkg !== "object") return null;
    const price = pkg.price;
    const includes = Array.isArray(pkg.includes) ? pkg.includes : [];
    if ((price === undefined || price === null) && includes.length === 0) return null;
    return { price, includes };
  };

  // Helper function to convert time string (HH:MM) to Firestore timestamp
  const timeStringToTimestamp = (timeString) => {
    if (!timeString || timeString.trim() === "") {
      return null;
    }

    // Parse HH:MM format
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      return null;
    }

    // Create a date for today with the specified time
    const today = new Date();
    today.setHours(hours, minutes, 0, 0);

    // Return as timestamp object (Firestore format)
    return {
      _seconds: Math.floor(today.getTime() / 1000),
      _nanoseconds: 0
    };
  };

  useEffect(() => {
    if (!initialServiceman || !initialServiceman.id) return;

    if (!isEditing) {
      setFormData({
        name: initialServiceman.name || "",
        email: initialServiceman.email || "",
        phone: initialServiceman.phone || "",
        profession: initialServiceman.profession || "",
        description: initialServiceman.description || "",
        location: initialServiceman.location || initialServiceman.address || "",
        areaName: initialServiceman.areaName || "",
        services: initialServiceman.services || [],
        subServices: initialServiceman.subServices || [],
        gender: initialServiceman.gender || "",
        vehicleTypes: initialServiceman.vehicleTypes || [],
        vehicleTypeData: initialServiceman.vehicleTypeData || [],
        brandExpertise: initialServiceman.brandExpertise || [],
        engineCC: initialServiceman.engineCC || [],
        fuelType: initialServiceman.fuelType || [],
        workshopType: initialServiceman.workshopType || [],
        availabilityStart: timestampToTimeString(initialServiceman.availabilityStart),
        availabilityEnd: timestampToTimeString(initialServiceman.availabilityEnd),
        isVerified: getServicemanVerified(initialServiceman),
        // Secondary profession fields
        secondaryProfession: normalizeProfessionValue(initialServiceman.secondaryProfession),
        secondaryDescription: initialServiceman.secondaryDescription || "",
        secondaryAreaName: initialServiceman.secondaryAreaName || "",
        secondaryServices: initialServiceman.secondaryServices || [],
        secondarySubServices: initialServiceman.secondarySubServices || [],
        secondaryVehicleTypeData: initialServiceman.secondaryVehicleTypeData || [],
        secondaryBrandExpertise: initialServiceman.secondaryBrandExpertise || [],
        secondaryEngineCC: initialServiceman.secondaryEngineCC || [],
        secondaryFuelType: initialServiceman.secondaryFuelType || [],
        secondaryWorkshopType: initialServiceman.secondaryWorkshopType || [],
        secondaryAvailabilityStart: timestampToTimeString(initialServiceman.secondaryAvailabilityStart),
        secondaryAvailabilityEnd: timestampToTimeString(initialServiceman.secondaryAvailabilityEnd),
      });
    }

    const fetchServicemanData = async () => {
      try {
        const url = `${BASE_URL}/servicemen/${initialServiceman.id}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("Failed to fetch updated serviceman data");
        }
        const updatedData = await response.json();
       
        const servicemanData = { ...updatedData, id: updatedData.id || initialServiceman.id };


        // Ensure secondaryMedia is properly formatted
        if (servicemanData.secondaryMedia) {
          // Convert to array if it's an object (Firestore sometimes returns objects)
          if (!Array.isArray(servicemanData.secondaryMedia)) {
            servicemanData.secondaryMedia = Object.values(servicemanData.secondaryMedia);
          }
          // Filter out any invalid items and ensure url exists
          servicemanData.secondaryMedia = servicemanData.secondaryMedia.filter(item =>
            item && item.url && typeof item.url === 'string' && item.url.trim() !== ''
          );
        }

        if (servicemanData.secondaryMedia && servicemanData.secondaryMedia.length > 0) {
          servicemanData.secondaryMedia.forEach((item, idx) => {
          });
        }

        // Initialize hidden media
        if (servicemanData.hidden) {
          if (!Array.isArray(servicemanData.hidden)) {
            servicemanData.hidden = Object.values(servicemanData.hidden);
          }
          servicemanData.hidden = servicemanData.hidden.filter(item =>
            item && item.url && typeof item.url === 'string' && item.url.trim() !== ''
          );
        }

        setServiceman(normalizeServicemanProfessionFields(servicemanData));

        // Only update media/documents state if NOT editing - preserve local changes during edit
        if (!isEditing) {
          setMedia((updatedData.media || []).filter(item => item && typeof item === 'object' && item.url));
          setSecondaryMedia((updatedData.secondaryMedia || []).filter(item => item && typeof item === 'object' && item.url));
          setDocuments((updatedData.documents || []).filter(item => item && typeof item === 'object'));
          setSecondaryDocuments((updatedData.secondaryDocuments || []).filter(item => item && typeof item === 'object'));
          setHiddenMedia((servicemanData.hidden || []).filter(item => item && item.url));
          setHiddenDocuments((servicemanData.hiddenDocuments || []).filter(item => item && (item?.file?.url || item?.url)));
          setHiddenSecondaryMedia((servicemanData.hiddenSecondary || []).filter(item => item && item.url));
          setHiddenSecondaryDocuments((servicemanData.hiddenSecondaryDocuments || []).filter(item => item && (item?.file?.url || item?.url)));
        }

        if (!isEditing) {
          setFormData((prev) => ({
            ...prev,
            name: updatedData.name || "",
            email: updatedData.email || "",
            phone: updatedData.phone || "",
            profession: updatedData.profession || "",
            description: updatedData.description || "",
            location: updatedData.location || updatedData.address || "",
            areaName: updatedData.areaName || "",
            services: updatedData.services || [],
            subServices: updatedData.subServices || [],
            gender: updatedData.gender || "",
            vehicleTypes: updatedData.vehicleTypes || [],
            vehicleTypeData: updatedData.vehicleTypeData || [],
            brandExpertise: updatedData.brandExpertise || [],
            engineCC: updatedData.engineCC || [],
            fuelType: updatedData.fuelType || [],
            workshopType: updatedData.workshopType || [],
            availabilityStart: timestampToTimeString(updatedData.availabilityStart),
            availabilityEnd: timestampToTimeString(updatedData.availabilityEnd),
            isVerified: getServicemanVerified(updatedData),
            secondaryProfession: normalizeProfessionValue(updatedData.secondaryProfession),
            secondaryDescription: updatedData.secondaryDescription || "",
            secondaryAreaName: updatedData.secondaryAreaName || "",
            secondaryServices: updatedData.secondaryServices || [],
            secondarySubServices: updatedData.secondarySubServices || [],
            secondaryVehicleTypeData: updatedData.secondaryVehicleTypeData || [],
            secondaryBrandExpertise: updatedData.secondaryBrandExpertise || [],
            secondaryEngineCC: updatedData.secondaryEngineCC || [],
            secondaryFuelType: updatedData.secondaryFuelType || [],
            secondaryWorkshopType: updatedData.secondaryWorkshopType || [],
            secondaryAvailabilityStart: timestampToTimeString(updatedData.secondaryAvailabilityStart),
            secondaryAvailabilityEnd: timestampToTimeString(updatedData.secondaryAvailabilityEnd),
          }));
        }
      } catch (error) {
        console.error("Error fetching serviceman data:", error);
      }
    };

    fetchServicemanData();
    const intervalId = setInterval(fetchServicemanData, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [initialServiceman?.id, isEditing]);

  // Fetch servicemenStats for the current serviceman
  useEffect(() => {
    const servicemanUid = serviceman?.uid || initialServiceman?.uid;

    if (!servicemanUid) {
      console.warn("[Frontend] No serviceman UID available for fetching stats");
      setServicemenStats([]);
      return;
    }

    const fetchServicemenStats = async () => {
      try {
        setLoadingStats(true);
        const url = `${BASE_URL}/servicemen/${servicemanUid}/stats`;

        const response = await fetch(url);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("[Frontend] Response error:", errorText);
          throw new Error(`Failed to fetch servicemen stats: ${response.status} ${errorText}`);
        }

        const stats = await response.json();

        if (Array.isArray(stats)) {
          setServicemenStats(stats);
        } else {
          console.warn("[Frontend] Stats is not an array:", stats);
          setServicemenStats([]);
        }
      } catch (error) {
        console.error("[Frontend] Error fetching servicemen stats:", error);
        setServicemenStats([]);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchServicemenStats();
  }, [serviceman?.uid, initialServiceman?.uid]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      // If profession changes, reset services and sub-services
      if (name === 'profession' || name === 'secondaryProfession') {
        if (name === 'profession') {
          newData.services = [];
          newData.subServices = [];
        } else {
          newData.secondaryServices = [];
          newData.secondarySubServices = [];
        }
      }
      return newData;
    });
  }, []);

  const handleEditClick = useCallback(() => {
    // Initialize form data with current serviceman data when editing starts
    setFormData({
      name: serviceman.name || "",
      email: serviceman.email || "",
      phone: serviceman.phone || "",
      profession: serviceman.profession || "",
      description: serviceman.description || "",
      location: serviceman.location || serviceman.address || "",
      areaName: serviceman.areaName || "",
      services: serviceman.services || [],
      subServices: serviceman.subServices || [],
      gender: serviceman.gender || "",
      vehicleTypes: serviceman.vehicleTypes || [],
      vehicleTypeData: serviceman.vehicleTypeData || [],
      brandExpertise: serviceman.brandExpertise || [],
      engineCC: serviceman.engineCC || [],
      fuelType: serviceman.fuelType || [],
      workshopType: serviceman.workshopType || [],
      availabilityStart: timestampToTimeString(serviceman.availabilityStart),
      availabilityEnd: timestampToTimeString(serviceman.availabilityEnd),
      isVerified: getServicemanVerified(serviceman),
      secondaryProfession: normalizeProfessionValue(serviceman.secondaryProfession),
      secondaryDescription: serviceman.secondaryDescription || "",
      secondaryAreaName: serviceman.secondaryAreaName || "",
      secondaryServices: serviceman.secondaryServices || [],
      secondarySubServices: serviceman.secondarySubServices || [],
      secondaryVehicleTypeData: serviceman.secondaryVehicleTypeData || [],
      secondaryBrandExpertise: serviceman.secondaryBrandExpertise || [],
      secondaryEngineCC: serviceman.secondaryEngineCC || [],
      secondaryFuelType: serviceman.secondaryFuelType || [],
      secondaryWorkshopType: serviceman.secondaryWorkshopType || [],
      secondaryAvailabilityStart: timestampToTimeString(serviceman.secondaryAvailabilityStart),
      secondaryAvailabilityEnd: timestampToTimeString(serviceman.secondaryAvailabilityEnd),
    });
    setMedia(serviceman.media || []);
    setSecondaryMedia(serviceman.secondaryMedia || []);
    setDocuments(serviceman.documents || []);
    setSecondaryDocuments(serviceman.secondaryDocuments || []);
    setIsEditing(true);
  }, [serviceman]);

  const handleServicesChange = useCallback((service) => {
    const scrollPosition = servicesContainerRef.current?.scrollTop || 0;
    setFormData((prev) => {
      const services = prev.services;
      const newServices = services.includes(service)
        ? services.filter((s) => s !== service)
        : [...services, service];
      return { ...prev, services: newServices };
    });
    setTimeout(() => {
      if (servicesContainerRef.current) {
        servicesContainerRef.current.scrollTop = scrollPosition;
      }
    }, 0);
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ["image/jpeg", "image/png", "video/mp4"];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(
      (file) => validTypes.includes(file.type) && file.size <= maxSize
    );
    if (validFiles.length !== files.length) {
      alert("Some files were invalid (only JPEG, PNG, MP4; max 10MB)");
    }
    setNewMediaFiles((prev) => [...prev, ...validFiles]);
  };

  const handleMediaClick = useCallback((item, index, isNew = false, mediaType = 'primary') => {
    if (isNew) {
      setSelectedMedia({ url: URL.createObjectURL(newMediaFiles[index]), type: "image" });
      setSelectedMediaIndex(-1);
      setSelectedMediaType('primary');
    } else {
      setSelectedMedia(item);
      setSelectedMediaIndex(index);
      setSelectedMediaType(mediaType);
    }
  }, [newMediaFiles]);

  const handleCloseMediaViewer = useCallback(() => {
    setSelectedMedia(null);
    setSelectedMediaIndex(-1);
  }, []);

  const handlePreviousMedia = useCallback(() => {
    setSelectedMediaIndex((prevIndex) => {
      const currentMediaArray = selectedMediaType === 'secondary'
        ? (serviceman?.secondaryMedia || [])
        : media;
      if (prevIndex > 0) {
        const newIndex = prevIndex - 1;
        setSelectedMedia(currentMediaArray[newIndex]);
        return newIndex;
      }
      return prevIndex;
    });
  }, [media, selectedMediaType, serviceman?.secondaryMedia]);

  const handleNextMedia = useCallback(() => {
    setSelectedMediaIndex((prevIndex) => {
      const currentMediaArray = selectedMediaType === 'secondary'
        ? (serviceman?.secondaryMedia || [])
        : media;
      if (prevIndex < currentMediaArray.length - 1) {
        const newIndex = prevIndex + 1;
        setSelectedMedia(currentMediaArray[newIndex]);
        return newIndex;
      }
      return prevIndex;
    });
  }, [media, selectedMediaType, serviceman?.secondaryMedia]);

  useEffect(() => {
    if (selectedMediaIndex >= 0) {
      const currentMediaArray = selectedMediaType === 'secondary'
        ? (serviceman?.secondaryMedia || [])
        : media;
      if (selectedMediaIndex < currentMediaArray.length) {
        setSelectedMedia(currentMediaArray[selectedMediaIndex]);
      }
    }
  }, [selectedMediaIndex, media, selectedMediaType, serviceman?.secondaryMedia]);

  useEffect(() => {
    if (!selectedMedia) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleCloseMediaViewer();
      } else if (e.key === "ArrowLeft") {
        handlePreviousMedia();
      } else if (e.key === "ArrowRight") {
        handleNextMedia();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMedia, handleCloseMediaViewer, handlePreviousMedia, handleNextMedia]);

  // Helper function to delete file from Firebase Storage
  const deleteFileFromStorage = async (fileUrl) => {
    try {
      if (!fileUrl || typeof fileUrl !== 'string') {
        console.warn("Invalid file URL for deletion:", fileUrl);
        return { success: false, error: "Invalid URL" };
      }

      if (!fileUrl.includes("firebasestorage.googleapis.com")) {
        console.warn("File URL is not a Firebase Storage URL:", fileUrl);
        return { success: false, error: "Not a Firebase Storage URL" };
      }

      // Extract the file path from the URL
      // Firebase Storage URL format: https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?token=TOKEN
      const urlParts = fileUrl.split("/");
      const oIndex = urlParts.indexOf("o");

      if (oIndex === -1 || oIndex >= urlParts.length - 1) {
        console.error("Could not parse Firebase Storage URL:", fileUrl);
        return { success: false, error: "Invalid URL format" };
      }

      const encodedPath = urlParts[oIndex + 1].split("?")[0];
      const decodedPath = decodeURIComponent(encodedPath);

      console.log("Attempting to delete file from storage:", decodedPath);
      const fileRef = ref(storage, decodedPath);

      try {
        await deleteObject(fileRef);
        console.log("✅ File successfully deleted from storage:", decodedPath);
        return { success: true };
      } catch (deleteError) {
        // Check if error is "object-not-found" - file might already be deleted
        if (deleteError.code === 'storage/object-not-found' ||
          deleteError.message?.includes('does not exist') ||
          deleteError.message?.includes('object-not-found')) {
          console.log("ℹ️ File already deleted or doesn't exist:", decodedPath);
          return { success: true, alreadyDeleted: true }; // Treat as success since file is gone
        }
        throw deleteError; // Re-throw other errors
      }
    } catch (error) {
      console.error("❌ Error deleting file from storage:", fileUrl, error);
      return { success: false, error: error.message || error.code || "Unknown error" };
    }
  };

  const handleHideMedia = useCallback(
    (index) => {
      try {
        const itemToHide = media[index];
        if (!itemToHide || !itemToHide.url) {
          console.warn("⚠️ Cannot hide media item - no URL:", itemToHide);
          return;
        }

        console.log("👁️ Hiding PRIMARY media item:", itemToHide, "at index:", index);

        // Add to hidden array
        setHiddenMedia((prev) => {
          const newHidden = [...prev, itemToHide];
          console.log("📝 Added to hidden media. Total hidden:", newHidden.length);
          return newHidden;
        });

        // Remove the item from the media array (delete the index)
        const updatedMedia = media.filter((_, i) => i !== index);
        setMedia(updatedMedia);
        console.log("✅ PRIMARY media hidden. Item removed from array.");
      } catch (error) {
        console.error("❌ Error hiding PRIMARY media:", error);
        alert("Failed to hide media");
      }
    },
    [media]
  );

  const handleHideDocument = useCallback(
    (index) => {
      try {
        const docToHide = documents[index];
        if (!docToHide) {
          console.warn("⚠️ Cannot hide document - no document:", docToHide);
          return;
        }

        const fileUrl = docToHide?.file?.url || docToHide?.url;
        if (!fileUrl) {
          console.warn("⚠️ Cannot hide document - no URL:", docToHide);
          return;
        }

        console.log("👁️ Hiding PRIMARY document:", docToHide, "at index:", index);

        // Add to hidden array
        setHiddenDocuments((prev) => {
          const newHidden = [...prev, docToHide];
          console.log("📝 Added to hidden documents. Total hidden:", newHidden.length);
          return newHidden;
        });

        // Remove the item from the documents array (delete the index)
        const updatedDocuments = documents.filter((_, i) => i !== index);
        setDocuments(updatedDocuments);
        console.log("✅ PRIMARY document hidden. Item removed from array.");
      } catch (error) {
        console.error("❌ Error hiding PRIMARY document:", error);
        alert("Failed to hide document");
      }
    },
    [documents]
  );

  const handleHideSecondaryMedia = useCallback(
    (index) => {
      try {
        const itemToHide = secondaryMedia[index];
        if (!itemToHide || !itemToHide.url) {
          console.warn("⚠️ Cannot hide secondary media item - no URL:", itemToHide);
          return;
        }
        console.log("👁️ Hiding SECONDARY media item:", itemToHide, "at index:", index);
        setHiddenSecondaryMedia((prev) => [...prev, itemToHide]);
        setSecondaryMedia((prev) => prev.filter((_, i) => i !== index)); // Remove from original array
        console.log("✅ SECONDARY media hidden. Media array updated.");
      } catch (error) {
        console.error("❌ Error hiding SECONDARY media:", error);
        alert("Failed to hide secondary media");
      }
    },
    [secondaryMedia]
  );

  const handleHideSecondaryDocument = useCallback(
    (index) => {
      try {
        const docToHide = secondaryDocuments[index];
        if (!docToHide) {
          console.warn("⚠️ Cannot hide secondary document - no document:", docToHide);
          return;
        }

        const fileUrl = docToHide?.file?.url || docToHide?.url;
        if (!fileUrl) {
          console.warn("⚠️ Cannot hide secondary document - no URL:", docToHide);
          return;
        }

        console.log("👁️ Hiding SECONDARY document:", docToHide, "at index:", index);

        // Add to hidden array
        setHiddenSecondaryDocuments((prev) => {
          const newHidden = [...prev, docToHide];
          console.log("📝 Added to hidden secondary documents. Total hidden:", newHidden.length);
          return newHidden;
        });

        // Remove the item from the documents array (delete the index)
        const updatedDocuments = secondaryDocuments.filter((_, i) => i !== index);
        setSecondaryDocuments(updatedDocuments);
        console.log("✅ SECONDARY document hidden. Item removed from array.");
      } catch (error) {
        console.error("❌ Error hiding SECONDARY document:", error);
        alert("Failed to hide secondary document");
      }
    },
    [secondaryDocuments]
  );

  const handleRemoveMedia = useCallback(
    async (index, isNew = false) => {
      try {
        if (isNew) {
          setNewMediaFiles((prev) => prev.filter((_, i) => i !== index));
        } else {
          const itemToRemove = media[index];
          console.log("🗑️ Removing PRIMARY media item:", itemToRemove, "at index:", index);

          // Skip if item is invalid
          if (!itemToRemove || !itemToRemove.url) {
            console.log("⚠️ Skipping removal - item is invalid or missing");
            return;
          }

          if (itemToRemove && itemToRemove.url) {
            // Add to deleted URLs list - will be deleted from storage on submit
            setDeletedMediaUrls((prev) => {
              if (!prev.includes(itemToRemove.url)) {
                console.log("📝 Added to deleted PRIMARY media URLs:", itemToRemove.url);
                return [...prev, itemToRemove.url];
              }
              console.log("⚠️ URL already in deleted list:", itemToRemove.url);
              return prev;
            });
          } else {
            console.warn("⚠️ PRIMARY media item has no URL:", itemToRemove);
          }

          const updatedMedia = media.filter((_, i) => i !== index);
          setMedia(updatedMedia);
          console.log("✅ PRIMARY media updated. Remaining items:", updatedMedia.length);
        }
      } catch (error) {
        console.error("❌ Error removing PRIMARY media:", error);
        alert("Failed to remove media");
      }
    },
    [media]
  );

  const handleRemoveSecondaryMedia = useCallback(
    async (index, isNew = false) => {
      try {
        if (isNew) {
          setNewSecondaryMediaFiles((prev) => prev.filter((_, i) => i !== index));
        } else {
          const itemToRemove = secondaryMedia[index];
          console.log("🗑️ Removing secondary media item:", itemToRemove, "at index:", index);

          if (itemToRemove && itemToRemove.url) {
            // Add to deleted URLs list - will be deleted from storage on submit
            setDeletedSecondaryMediaUrls((prev) => {
              if (!prev.includes(itemToRemove.url)) {
                console.log("📝 Added to deleted secondary media URLs:", itemToRemove.url);
                return [...prev, itemToRemove.url];
              }
              console.log("⚠️ URL already in deleted list:", itemToRemove.url);
              return prev;
            });
          } else {
            console.warn("⚠️ Secondary media item has no URL:", itemToRemove);
          }

          const updatedSecondaryMedia = secondaryMedia.filter((_, i) => i !== index);
          setSecondaryMedia(updatedSecondaryMedia);
          console.log("✅ Secondary media updated. Remaining items:", updatedSecondaryMedia.length);
        }
      } catch (error) {
        console.error("❌ Error removing secondary media:", error);
        alert("Failed to remove secondary media");
      }
    },
    [secondaryMedia]
  );

  const handleRemoveDocument = useCallback(
    async (index, isNew = false) => {
      try {
        if (isNew) {
          setNewDocumentFiles((prev) => prev.filter((_, i) => i !== index));
        } else {
          const docToRemove = documents[index];

          // Skip if document is invalid
          if (!docToRemove) {
            console.log("⚠️ Skipping removal - document is invalid or missing");
            return;
          }

          console.log("🗑️ Removing primary document:", docToRemove, "at index:", index);

          // Handle both document structures: {file: {url}} or {url}
          const fileUrl = docToRemove?.file?.url || docToRemove?.url;
          if (fileUrl) {
            // Add to deleted URLs list - will be deleted from storage on submit
            setDeletedDocumentUrls((prev) => {
              if (!prev.includes(fileUrl)) {
                console.log("📝 Added to deleted primary document URLs:", fileUrl);
                return [...prev, fileUrl];
              }
              console.log("⚠️ URL already in deleted list:", fileUrl);
              return prev;
            });
          } else {
            console.warn("⚠️ Document has no URL:", docToRemove);
          }

          const updatedDocuments = documents.filter((_, i) => i !== index);
          setDocuments(updatedDocuments);
          console.log("✅ Primary documents updated. Remaining items:", updatedDocuments.length);
        }
      } catch (error) {
        console.error("❌ Error removing primary document:", error);
        alert("Failed to remove document");
      }
    },
    [documents]
  );

  const handleRemoveSecondaryDocument = useCallback(
    async (index, isNew = false) => {
      try {
        if (isNew) {
          setNewSecondaryDocumentFiles((prev) => prev.filter((_, i) => i !== index));
        } else {
          const docToRemove = secondaryDocuments[index];
          console.log("🗑️ Removing secondary document:", docToRemove, "at index:", index);

          // Handle both document structures: {file: {url}} or {url}
          const fileUrl = docToRemove?.file?.url || docToRemove?.url;
          if (fileUrl) {
            // Add to deleted URLs list - will be deleted from storage on submit
            setDeletedSecondaryDocumentUrls((prev) => {
              if (!prev.includes(fileUrl)) {
                console.log("📝 Added to deleted secondary document URLs:", fileUrl);
                return [...prev, fileUrl];
              }
              console.log("⚠️ URL already in deleted list:", fileUrl);
              return prev;
            });
          } else {
            console.warn("⚠️ Secondary document has no URL:", docToRemove);
          }

          const updatedSecondaryDocuments = secondaryDocuments.filter((_, i) => i !== index);
          setSecondaryDocuments(updatedSecondaryDocuments);
          console.log("✅ Secondary documents updated. Remaining items:", updatedSecondaryDocuments.length);
        }
      } catch (error) {
        console.error("❌ Error removing secondary document:", error);
        alert("Failed to remove secondary document");
      }
    },
    [secondaryDocuments]
  );

  const uploadFiles = async (files, folder = "media") => {
    const uploadedMedia = [];
    for (const file of files) {
      const fileType = file.type.startsWith("image/") ? "image" : "video";
      const storageRef = ref(storage, `${folder}/${serviceman.id}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      uploadedMedia.push({ url, type: fileType });
    }
    return uploadedMedia;
  };

  const uploadDocumentFiles = async (files, folder = "documents") => {
    const uploadedDocs = [];
    for (const file of files) {
      const storageRef = ref(storage, `${folder}/${serviceman.id}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const fileType = file.name.split('.').pop().toLowerCase();
      uploadedDocs.push({
        file: {
          url,
          name: file.name,
          type: fileType,
        },
        title: file.name,
      });
    }
    return uploadedDocs;
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      setProfilePictureFile(file);
      setProfilePicturePreview(URL.createObjectURL(file));
    }
  };

  const handleSecondaryMediaFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ["image/jpeg", "image/png", "video/mp4"];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(
      (file) => validTypes.includes(file.type) && file.size <= maxSize
    );
    if (validFiles.length !== files.length) {
      alert("Some files were invalid (only JPEG, PNG, MP4; max 10MB)");
    }
    setNewSecondaryMediaFiles((prev) => [...prev, ...validFiles]);
  };

  const handleDocumentFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewDocumentFiles((prev) => [...prev, ...files]);
  };

  const handleSecondaryDocumentFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewSecondaryDocumentFiles((prev) => [...prev, ...files]);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        // Handle profile picture deletion - DELETE FROM STORAGE FIRST
        const profilePictureUrl = serviceman.image;
        let imageUrl = serviceman.image || DEFAULT_IMAGE;

        // If profile picture is deleted, delete from storage and set to default image
        if (isProfilePictureDeleted && profilePictureUrl && profilePictureUrl !== DEFAULT_IMAGE) {
          console.log("🗑️ Deleting profile picture from Firebase Storage:", profilePictureUrl);

          // Delete profile picture from Firebase Storage
          const deleteResult = await deleteFileFromStorage(profilePictureUrl);
          if (deleteResult.success) {
            console.log("✅ Profile picture successfully deleted from storage");
          } else {
            console.warn("⚠️ Failed to delete profile picture from storage, but continuing with update:", deleteResult.error);
            // Continue anyway - we'll still update Firestore
          }

          // Set to default image
          imageUrl = DEFAULT_IMAGE;
        }

        // Ensure imageUrl is always a valid string (never null, undefined, or empty)
        if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
          imageUrl = DEFAULT_IMAGE;
        }

        // Delete all other files marked for deletion from Firebase Storage
        // Exclude profile picture from media deletion list (already handled above)
        const mediaUrlsToDelete = deletedMediaUrls.filter(url => url !== profilePictureUrl);
        const allDeletedUrls = [
          ...mediaUrlsToDelete,
          ...deletedSecondaryMediaUrls,
          ...deletedDocumentUrls,
          ...deletedSecondaryDocumentUrls
        ];

        if (allDeletedUrls.length > 0) {
          console.log("🗑️ Files to delete from storage:", {
            primaryMedia: mediaUrlsToDelete,
            secondaryMedia: deletedSecondaryMediaUrls,
            primaryDocuments: deletedDocumentUrls,
            secondaryDocuments: deletedSecondaryDocumentUrls,
            total: allDeletedUrls.length
          });

          // Delete files from storage in parallel
          const deletePromises = allDeletedUrls.map(async (url) => {
            const result = await deleteFileFromStorage(url);
            return { url, ...result };
          });

          const deleteResults = await Promise.all(deletePromises);
          const failedDeletes = deleteResults.filter(r => !r.success);
          const successfulDeletes = deleteResults.filter(r => r.success);

          if (successfulDeletes.length > 0) {
            console.log(`✅ ${successfulDeletes.length} file(s) successfully deleted from storage`);
          }

          if (failedDeletes.length > 0) {
            console.warn("⚠️ Some files failed to delete (but continuing with Firestore update):", failedDeletes);
            // Continue anyway - we'll still update Firestore to remove the references
          }

          console.log("📝 Proceeding with Firestore update to remove media/document references");
        }

        // Use current media arrays (no new uploads)
        // Hidden items are already removed from arrays, just filter out invalid items
        const updatedMedia = media.filter(item => item && typeof item === 'object' && item.url);
        const updatedSecondaryMedia = [...secondaryMedia];
        // Hidden items are already removed from arrays, just filter out invalid items
        const updatedDocuments = documents.filter(item => item && typeof item === 'object');
        const updatedSecondaryDocuments = [...secondaryDocuments];

        // Prepare update data with all fields
        // IMPORTANT: Only include fields that should be updated
        // Exclude availability times and other read-only fields to prevent type issues
        const updatedData = {
          // Basic info
          name: formData.name || serviceman.name || '',
          email: formData.email || serviceman.email || '',
          phone: formData.phone || serviceman.phone || '',
          gender: formData.gender || null,
          dateOfBirth: formData.dateOfBirth || null,
          address: formData.address || null,
          city: formData.city || null,
          latitude: formData.latitude || null,
          longitude: formData.longitude || null,
          description: formData.description || null,
          // Profession fields
          profession: formData.profession || null,
          services: formData.services || [],
          subServices: formData.subServices || [],
          vehicleTypes: formData.vehicleTypes || [],
          vehicleTypeData: formData.vehicleTypeData || [],
          brandExpertise: formData.brandExpertise || [],
          engineCC: formData.engineCC || [],
          fuelType: formData.fuelType || [],
          workshopType: formData.workshopType || [],
          isVerified: !!formData.isVerified,
          verified: !!formData.isVerified,
          // Secondary profession fields
          secondaryProfession: formData.secondaryProfession || null,
          secondaryDescription: formData.secondaryDescription || null,
          secondaryServices: formData.secondaryServices || [],
          secondarySubServices: formData.secondarySubServices || [],
          secondaryVehicleTypeData: formData.secondaryVehicleTypeData || [],
          secondaryBrandExpertise: formData.secondaryBrandExpertise || [],
          secondaryEngineCC: formData.secondaryEngineCC || [],
          secondaryFuelType: formData.secondaryFuelType || [],
          secondaryWorkshopType: formData.secondaryWorkshopType || [],
          // NOTE: availabilityStart, availabilityEnd, secondaryAvailabilityStart, secondaryAvailabilityEnd
          // are NOT included - they are read-only and should not be modified
        };

        // Handle media and documents - filter out invalid items, but don't include empty strings
        // Hidden items are already removed from arrays, so we just need to validate remaining items
        const validMedia = media.filter(item => {
          if (!item || typeof item !== 'object') {
            console.warn("⚠️ Invalid media item (not an object):", item);
            return false; // Skip invalid items
          }

          // Validate item structure
          if (!item.url || !item.type || !["image", "video"].includes(item.type)) {
            console.warn("⚠️ Invalid media item structure:", item);
            return false; // Skip invalid items
          }

          return true; // Keep valid items
        });
        updatedData.media = validMedia.length > 0 ? validMedia : [];
        updatedData.secondaryMedia = updatedSecondaryMedia.length > 0 ? updatedSecondaryMedia : [];

        // Handle documents: filter out invalid items, hidden items are already removed
        const validDocuments = documents.filter(doc => {
          if (!doc || typeof doc !== 'object') {
            console.warn("⚠️ Invalid document item:", doc);
            return false; // Skip invalid items
          }

          const fileUrl = doc?.file?.url || doc?.url;
          if (!fileUrl) {
            console.warn("⚠️ Document missing URL:", doc);
            return false; // Skip items without URL
          }

          return true; // Keep valid documents
        });
        updatedData.documents = validDocuments.length > 0 ? validDocuments : [];
        updatedData.secondaryDocuments = updatedSecondaryDocuments.length > 0 ? updatedSecondaryDocuments : [];

        // Save hidden media and documents arrays
        // Only include if they have items - backend will handle deletion if needed
        if (hiddenMedia.length > 0) {
          updatedData.hidden = hiddenMedia;
        }
        // Don't include hidden field if empty - backend will handle it
        if (hiddenDocuments.length > 0) {
          updatedData.hiddenDocuments = hiddenDocuments;
        }
        // Don't include hiddenDocuments field if empty - backend will handle it
        if (hiddenSecondaryMedia.length > 0) {
          updatedData.hiddenSecondary = hiddenSecondaryMedia;
        }
        // Don't include hiddenSecondary field if empty - backend will handle it
        if (hiddenSecondaryDocuments.length > 0) {
          updatedData.hiddenSecondaryDocuments = hiddenSecondaryDocuments;
        }
        // Don't include hiddenSecondaryDocuments field if empty - backend will handle it

        // Handle image field - always ensure it's a valid string URL
        // This prevents mobile app crashes when image is null/undefined
        // CRITICAL: Ensure imageUrl is always a string, never null/undefined/object/empty string
        if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
          imageUrl = DEFAULT_IMAGE;
        }
        // CRITICAL: Always set to DEFAULT_IMAGE if profile picture was deleted, never empty string
        if (isProfilePictureDeleted) {
          imageUrl = DEFAULT_IMAGE;
        }
        updatedData.image = String(imageUrl).trim() || DEFAULT_IMAGE; // Explicitly convert to string and ensure it's not empty

        // Remove null/empty fields to avoid overwriting with null (except media/documents/image which we handle above)
        // Also ensure we don't send any fields that could cause type issues
        Object.keys(updatedData).forEach(key => {
          // Skip fields that we explicitly want to keep (even if null/empty)
          if (key === 'image' || key === 'media' || key === 'secondaryMedia' ||
            key === 'documents' || key === 'secondaryDocuments' || key === 'hidden' || key === 'hiddenDocuments' ||
            key === 'hiddenSecondary' || key === 'hiddenSecondaryDocuments') {
            return;
          }

          // Remove null, empty strings, or empty arrays
          if (updatedData[key] === null || updatedData[key] === "" ||
            (Array.isArray(updatedData[key]) && updatedData[key].length === 0)) {
            delete updatedData[key];
          }

          // Ensure no complex objects that could cause serialization issues
          // (except arrays and objects that are explicitly allowed)
          if (typeof updatedData[key] === 'object' && updatedData[key] !== null &&
            !Array.isArray(updatedData[key]) &&
            key !== 'vehicleTypeData' && key !== 'secondaryVehicleTypeData') {
            console.warn(`Removing complex object field ${key} to prevent type issues`);
            delete updatedData[key];
          }
        });

        const url = `${BASE_URL}/servicemen/${serviceman.id}`;

        // Log the data being sent for debugging
        console.log("📤 Sending update data:", {
          media: updatedData.media?.length || 0,
          documents: updatedData.documents?.length || 0,
          hidden: updatedData.hidden?.length || 0,
          hiddenDocuments: updatedData.hiddenDocuments?.length || 0,
          secondaryMedia: updatedData.secondaryMedia?.length || 0,
          secondaryDocuments: updatedData.secondaryDocuments?.length || 0,
          hiddenSecondary: updatedData.hiddenSecondary?.length || 0,
          hiddenSecondaryDocuments: updatedData.hiddenSecondaryDocuments?.length || 0,
        });
        // Log media array structure for debugging
        if (updatedData.media && updatedData.media.length > 0) {
          console.log("📤 Media array items:", updatedData.media.map((item, idx) => ({
            index: idx,
            isEmpty: item === "" || item === null,
            hasUrl: item?.url ? true : false,
            hasType: item?.type ? true : false,
            type: item?.type,
          })));
        }

        const response = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedData),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("❌ Update failed:", response.status, errorData);
          throw new Error(errorData.error || `Failed to update serviceman data: ${response.status}`);
        }

        const updatedServiceman = await response.json();
        const merged = updatedServiceman.data || updatedServiceman;
        setServiceman(normalizeServicemanProfessionFields(merged));
        // Filter out invalid items when setting media state (no empty strings expected)
        const filteredMedia = (updatedServiceman.data?.media || updatedServiceman.media || []).filter(
          item => item && typeof item === 'object' && item.url
        );
        setMedia(filteredMedia);
        setSecondaryMedia(updatedSecondaryMedia);
        // Filter out invalid items when setting documents state (no empty strings expected)
        const filteredDocuments = (updatedServiceman.data?.documents || updatedServiceman.documents || []).filter(
          item => item && typeof item === 'object'
        );
        setDocuments(filteredDocuments);
        setSecondaryDocuments(updatedSecondaryDocuments);
        setHiddenMedia((updatedServiceman.data?.hidden || updatedServiceman.hidden || []).filter(item => item && item.url));
        setHiddenDocuments((updatedServiceman.data?.hiddenDocuments || updatedServiceman.hiddenDocuments || []).filter(item => item && (item?.file?.url || item?.url)));
        setHiddenSecondaryMedia((updatedServiceman.data?.hiddenSecondary || updatedServiceman.hiddenSecondary || []).filter(item => item && item.url));
        setHiddenSecondaryDocuments((updatedServiceman.data?.hiddenSecondaryDocuments || updatedServiceman.hiddenSecondaryDocuments || []).filter(item => item && (item?.file?.url || item?.url)));
        setProfilePictureFile(null);
        setProfilePicturePreview(null);
        setNewMediaFiles([]);
        setNewSecondaryMediaFiles([]);
        setNewDocumentFiles([]);
        setNewSecondaryDocumentFiles([]);
        setDeletedMediaUrls([]);
        setDeletedDocumentUrls([]);
        setDeletedSecondaryMediaUrls([]);
        setDeletedSecondaryDocumentUrls([]);
        setIsProfilePictureDeleted(false);
        setIsEditing(false);
        alert("Serviceman profile updated successfully!");
      } catch (error) {
        console.error("Error updating serviceman:", error);
        alert("Failed to update serviceman data: " + error.message);
      }
    },
    [formData, serviceman?.id, serviceman?.image, media, newMediaFiles, secondaryMedia, newSecondaryMediaFiles,
      documents, newDocumentFiles, secondaryDocuments, newSecondaryDocumentFiles, profilePictureFile,
      isProfilePictureDeleted, deletedMediaUrls, deletedSecondaryMediaUrls, deletedDocumentUrls, deletedSecondaryDocumentUrls,
      hiddenMedia, hiddenDocuments, hiddenSecondaryMedia, hiddenSecondaryDocuments]
  );

  // Helper function to format field values
  const formatFieldValue = (value) => {
    if (value === null || value === undefined) {
      return "N/A";
    }

    // Handle Firestore timestamps
    if (value && typeof value === 'object' && value._seconds) {
      return new Date(value._seconds * 1000).toLocaleString();
    }

    // Handle empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return "N/A";
    }

    // Handle objects (but not arrays, as arrays are handled separately in JSX)
    if (typeof value === 'object' && !Array.isArray(value)) {
      return JSON.stringify(value, null, 2);
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  };

  // Helper function to format timestamp to date/time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp._seconds != null) {
      return new Date(timestamp._seconds * 1000).toLocaleString();
    }
    if (timestamp && typeof timestamp.toDate === "function") {
      try {
        const d = timestamp.toDate();
        if (!isNaN(d.getTime())) return d.toLocaleString();
      } catch {
        /* ignore */
      }
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    if (typeof timestamp === "string") {
      return new Date(timestamp).toLocaleString();
    }
    return formatFieldValue(timestamp);
  };

  const formatDateOfBirthValue = (dob) => {
    if (dob == null || dob === "") return "N/A";
    if (typeof dob === "object" && dob._seconds != null) {
      return new Date(dob._seconds * 1000).toLocaleDateString();
    }
    if (typeof dob === "object" && typeof dob.toDate === "function") {
      try {
        const d = dob.toDate();
        if (!isNaN(d.getTime())) return d.toLocaleDateString();
      } catch {
        /* ignore */
      }
    }
    if (dob instanceof Date && !isNaN(dob.getTime())) return dob.toLocaleDateString();
    return formatFieldValue(dob);
  };

  // Check if single profession (not dual profession)
  const isSingleProfession = () => {
    return !serviceman.professions || (Array.isArray(serviceman.professions) && serviceman.professions.length <= 1);
  };

  // Check if secondary profession exists
  const hasSecondaryProfession = () => {
  return (
    serviceman?.enableSecondaryProfession === true &&
    normalizeProfessionValue(serviceman?.secondaryProfession) !== ""
  );
};


  // Format vehicle type data for display
  const formatVehicleTypeData = (vehicleTypeData) => {
    if (!vehicleTypeData || !Array.isArray(vehicleTypeData) || vehicleTypeData.length === 0) {
      return null;
    }

    return vehicleTypeData.map((vt, index) => ({
      vehicleType: vt.vehicleType || `Vehicle Type ${index + 1}`,
      services: Array.isArray(vt.services) ? vt.services : [],
      subServices: Array.isArray(vt.subServices) ? vt.subServices : [],
      brandExpertise: Array.isArray(vt.brandExpertise) ? vt.brandExpertise : [],
      engineCC: Array.isArray(vt.engineCC) ? vt.engineCC : [],
      fuelType: Array.isArray(vt.fuelType) ? vt.fuelType : [],
      workshopType: Array.isArray(vt.workshopType) ? vt.workshopType : [],
    }));
  };

  if (!serviceman) {
    return (
      <div className="error">
        <p>No serviceman data available</p>
        <button className="back-btn" onClick={() => navigate("/servicemen")}>
          Back to Servicemen
        </button>
      </div>
    );
  }

  const imageSrc = serviceman.image || DEFAULT_IMAGE;
  const dob = formatDateOfBirthValue(serviceman.dateOfBirth);
  const lastSeen = formatTimestamp(serviceman.lastSeen || serviceman.profileUpdatedAt);
  const servicemanVerified = getServicemanVerified(serviceman);
  const primaryFsp = formatFullServicePackage(serviceman.fullServicePackage);
  const secondaryFsp = formatFullServicePackage(serviceman.secondaryFullServicePackage);

  // Get vehicle type data
  const vehicleTypeData = serviceman.vehicleTypeData ? formatVehicleTypeData(serviceman.vehicleTypeData) : null;
  const secondaryVehicleTypeData = serviceman.secondaryVehicleTypeData ? formatVehicleTypeData(serviceman.secondaryVehicleTypeData) : null;

  // Format availability times
  const formatAvailabilityTime = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp._seconds != null) {
      return new Date(timestamp._seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (timestamp && typeof timestamp.toDate === "function") {
      try {
        const d = timestamp.toDate();
        if (!isNaN(d.getTime())) {
          return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      } catch {
        /* ignore */
      }
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return formatFieldValue(timestamp);
  };

  const primaryAvailabilityStart = formatAvailabilityTime(serviceman.availabilityStart);
  const primaryAvailabilityEnd = formatAvailabilityTime(serviceman.availabilityEnd);
  const primaryAvailability = primaryAvailabilityStart !== "N/A" && primaryAvailabilityEnd !== "N/A"
    ? `${primaryAvailabilityStart} - ${primaryAvailabilityEnd}`
    : "N/A";

  const secondaryAvailabilityStart = formatAvailabilityTime(serviceman.secondaryAvailabilityStart);
  const secondaryAvailabilityEnd = formatAvailabilityTime(serviceman.secondaryAvailabilityEnd);
  const secondaryAvailability = secondaryAvailabilityStart !== "N/A" && secondaryAvailabilityEnd !== "N/A"
    ? `${secondaryAvailabilityStart} - ${secondaryAvailabilityEnd}`
    : "N/A";

  return (
    <ServicemanCorrectionDisplayContext.Provider value={{ serviceman, pendingCorrectionFields: correctionFields }}>
    <div className="serviceman-profile">
      <div className="header">
        <h2>Serviceman Profile</h2>
        <div className="button-group">
          {canEditServiceman && (
            <div
              className="correction-save-wrap"
              onMouseEnter={openCorrectionPreview}
              onMouseLeave={scheduleCloseCorrectionPreview}
            >
              <div
                className="correction-save-btn"
                role="button"
                tabIndex={savingCorrections ? -1 : 0}
                aria-disabled={savingCorrections}
                onClick={() => {
                  if (savingCorrections) return;
                  handleSaveCorrections();
                }}
                onKeyDown={(e) => {
                  if (savingCorrections) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSaveCorrections();
                  }
                }}
                title={hasMarkedCorrections ? "Marked for correction" : "Mark for correction"}
              >
                {savingCorrections
                  ? "Saving…"
                  : hasMarkedCorrections
                    ? "Marked for correction"
                    : "Mark for correction"}
              </div>
              {correctionHoverOpen && (
                <div
                  className="correction-preview-panel"
                  role="region"
                  aria-label="Correction entries preview"
                  onMouseEnter={openCorrectionPreview}
                  onMouseLeave={scheduleCloseCorrectionPreview}
                >
                  {correctionPreviewEntries.length > 0 ? (
                  <ul className="correction-preview-list">
                    {correctionPreviewEntries.map(({ key, current }) => (
                      <li key={key} className="correction-preview-row">
                        <div className="correction-preview-row-main">
                          <span className="correction-preview-key">{key}</span>
                          <div
                            className="correction-preview-value correction-preview-value--rich correction-preview-present"
                            data-field={key}
                          >
                            <CorrectionRichValue value={current} fieldKey={key} />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="correction-preview-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCorrectionPreviewRow(key);
                          }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                  ) : (
                    <p className="correction-preview-empty">
                      No fields are checked. Apply to save an empty correction list and clear marks on the server.
                    </p>
                  )}
                  <div className="correction-preview-footer">
                    <button
                      type="button"
                      className="correction-preview-apply-btn"
                      disabled={savingCorrections}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveCorrections();
                      }}
                    >
                      {savingCorrections ? "Saving…" : "Apply changes"}
                    </button>
                    <p className="correction-preview-footer-hint">Use Apply to write changes to Firestore.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          {canEditServiceman && (
            <button className="edit-btn" onClick={handleEditClick}>
              Edit Profile
            </button>
          )}
          <button className="back-btn" onClick={() => navigate("/servicemen")}>
            Back
          </button>
        </div>
      </div>

      {/* Basic Information — bento grid + correction toggles */}
      <div className="profile-container profile-bento-wrap">
        <h3 className="section-title">Basic Information</h3>
        <div className="profile-bento-grid">
          <article className={`profile-bento-card profile-bento-card--wide profile-bento-card--media${isCorrectionCardHighlighted(correctionFields, serviceman, "image") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Profile picture</span>
              {canEditServiceman && !isCorrectionKeyExcluded("image") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "image")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("image")}
                  label="Profile picture"
                />
              )}
            </div>
            <div className="profile-bento-value profile-bento-value--media">
              <CorrectionFieldDualCompare fieldKey="image">
                <img
                  src={imageSrc}
                  alt="Profile"
                  className="profile-pic profile-pic--bento"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowProfilePictureFullscreen(true)}
                  onError={(e) => {
                    e.target.src = DEFAULT_IMAGE;
                  }}
                />
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "name") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Name</span>
              {canEditServiceman && !isCorrectionKeyExcluded("name") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "name")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("name")}
                  label="Name"
                />
              )}
            </div>
            <div className="profile-bento-value profile-bento-value--row">
              <CorrectionFieldDualCompare fieldKey="name">
                <span>{serviceman.name || "N/A"}</span>
                {servicemanVerified && (
                  <img src="/blue tick.png" alt="Verified" style={{ width: "22px", height: "22px" }} title="Verified" />
                )}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "email") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Email</span>
              {canEditServiceman && !isCorrectionKeyExcluded("email") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "email")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("email")}
                  label="Email"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="email">{serviceman.email || "N/A"}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "phone") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Phone</span>
              {canEditServiceman && !isCorrectionKeyExcluded("phone") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "phone")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("phone")}
                  label="Phone"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="phone">{serviceman.phone || "N/A"}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "verified") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Verified</span>
              {canEditServiceman && !isCorrectionKeyExcluded("verified") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "verified")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("verified")}
                  label="Verified"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="verified">{servicemanVerified ? "Yes" : "No"}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "dateOfBirth") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Date of birth</span>
              {canEditServiceman && !isCorrectionKeyExcluded("dateOfBirth") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "dateOfBirth")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("dateOfBirth")}
                  label="Date of birth"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="dateOfBirth">{dob}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card profile-bento-card--wide${isCorrectionCardHighlighted(correctionFields, serviceman, "location") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Location</span>
              {canEditServiceman && !isCorrectionKeyExcluded("location") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "location")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("location")}
                  label="Location"
                />
              )}
            </div>
            <div className="profile-bento-value profile-bento-value--multiline">
              <CorrectionFieldDualCompare fieldKey="location">
                {serviceman.location || serviceman.address || "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "areaName") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Primary area</span>
              {canEditServiceman && !isCorrectionKeyExcluded("areaName") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "areaName")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("areaName")}
                  label="Primary area"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="areaName">{serviceman.areaName || "N/A"}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "deviceType") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Device type</span>
              {canEditServiceman && !isCorrectionKeyExcluded("deviceType") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "deviceType")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("deviceType")}
                  label="Device type"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="deviceType">
                {serviceman.deviceType && serviceman.deviceType.trim() !== "" ? serviceman.deviceType : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card profile-bento-card--wide${isCorrectionCardHighlighted(correctionFields, serviceman, "lastSeen") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Last seen</span>
              {canEditServiceman && !isCorrectionKeyExcluded("lastSeen") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "lastSeen")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("lastSeen")}
                  label="Last seen"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="lastSeen">{lastSeen}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card profile-bento-card--wide${isCorrectionCardHighlighted(correctionFields, serviceman, "aadharCard") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Aadhar card</span>
              {canEditServiceman && !isCorrectionKeyExcluded("aadharCard") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "aadharCard")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("aadharCard")}
                  label="Aadhar card"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="aadharCard">
                {serviceman.aadharCard && serviceman.aadharCard.url ? (
                  <div className="profile-aadhar-inline">
                    <CorrectionImageWithLink
                      url={serviceman.aadharCard.url}
                      alt={serviceman.aadharCard.name || "Aadhar document"}
                      treatAsImage
                    />
                  </div>
                ) : (
                  "N/A"
                )}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "amountPaid") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Amount paid</span>
              {canEditServiceman && !isCorrectionKeyExcluded("amountPaid") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "amountPaid")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("amountPaid")}
                  label="Amount paid"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="amountPaid">
                {serviceman.amountPaid !== undefined && serviceman.amountPaid !== null
                  ? `₹${serviceman.amountPaid.toLocaleString("en-IN")}`
                  : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "havePaid") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Have paid</span>
              {canEditServiceman && !isCorrectionKeyExcluded("havePaid") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "havePaid")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("havePaid")}
                  label="Have paid"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="havePaid">
                {serviceman.havePaid === true || serviceman.havePaid === "true" || serviceman.havePaid === "True"
                  ? "Yes"
                  : serviceman.havePaid === false || serviceman.havePaid === "false" || serviceman.havePaid === "False"
                    ? "No"
                    : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>
        </div>
      </div>

      {/* Account, referral, and engagement — bento grid */}
      <div className="profile-container profile-bento-wrap">
        <h3 className="section-title">Account &amp; activity</h3>
        <div className="profile-bento-grid">
          <article className={`profile-bento-card profile-bento-card--wide${isCorrectionCardHighlighted(correctionFields, serviceman, "uid") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">User ID</span>
              {canEditServiceman && !isCorrectionKeyExcluded("uid") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "uid")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("uid")}
                  label="User ID"
                />
              )}
            </div>
            <div className="profile-bento-value profile-bento-value--mono">
              <CorrectionFieldDualCompare fieldKey="uid">{serviceman.uid || "N/A"}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "referralCode") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Referral code</span>
              {canEditServiceman && !isCorrectionKeyExcluded("referralCode") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "referralCode")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("referralCode")}
                  label="Referral code"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="referralCode">
                {serviceman.referralCode != null && String(serviceman.referralCode).trim() !== ""
                  ? String(serviceman.referralCode)
                  : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "couponCode") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Coupon code</span>
              {canEditServiceman && !isCorrectionKeyExcluded("couponCode") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "couponCode")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("couponCode")}
                  label="Coupon code"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="couponCode">
                {serviceman.couponCode != null && String(serviceman.couponCode).trim() !== ""
                  ? String(serviceman.couponCode)
                  : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "couponDiscount") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Coupon discount</span>
              {canEditServiceman && !isCorrectionKeyExcluded("couponDiscount") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "couponDiscount")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("couponDiscount")}
                  label="Coupon discount"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="couponDiscount">
                {serviceman.couponDiscount !== undefined && serviceman.couponDiscount !== null
                  ? String(serviceman.couponDiscount)
                  : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "profileComplete") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Profile complete</span>
              {canEditServiceman && !isCorrectionKeyExcluded("profileComplete") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "profileComplete")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("profileComplete")}
                  label="Profile complete"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="profileComplete">
                {serviceman.profileComplete === true
                  ? "Yes"
                  : serviceman.profileComplete === false
                    ? "No"
                    : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card profile-bento-card--wide${isCorrectionCardHighlighted(correctionFields, serviceman, "createdAt") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Account created</span>
              {canEditServiceman && !isCorrectionKeyExcluded("createdAt") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "createdAt")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("createdAt")}
                  label="Account created"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="createdAt">{formatTimestamp(serviceman.createdAt)}</CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "isOnline") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Online now</span>
              {canEditServiceman && !isCorrectionKeyExcluded("isOnline") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "isOnline")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("isOnline")}
                  label="Online now"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="isOnline">
                {serviceman.isOnline === true ? "Yes" : serviceman.isOnline === false ? "No" : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "bookingsCount") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Bookings count</span>
              {canEditServiceman && !isCorrectionKeyExcluded("bookingsCount") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "bookingsCount")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("bookingsCount")}
                  label="Bookings count"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="bookingsCount">
                {serviceman.bookingsCount != null ? String(serviceman.bookingsCount) : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "callNowCount") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Call now count</span>
              {canEditServiceman && !isCorrectionKeyExcluded("callNowCount") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "callNowCount")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("callNowCount")}
                  label="Call now count"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="callNowCount">
                {serviceman.callNowCount != null ? String(serviceman.callNowCount) : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "impressions") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">Impressions</span>
              {canEditServiceman && !isCorrectionKeyExcluded("impressions") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "impressions")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("impressions")}
                  label="Impressions"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="impressions">
                {serviceman.impressions != null ? String(serviceman.impressions) : "N/A"}
              </CorrectionFieldDualCompare>
            </div>
          </article>

          <article className={`profile-bento-card${isCorrectionCardHighlighted(correctionFields, serviceman, "mpin") ? " is-marked" : ""}`}>
            <div className="profile-bento-card-head">
              <span className="profile-bento-label">MPIN</span>
              {canEditServiceman && !isCorrectionKeyExcluded("mpin") && (
                <ProfileCorrectionToggle
                  active={isFieldMarkedForCorrection(correctionFields, "mpin")}
                  disabled={!canEditServiceman}
                  onToggle={() => toggleCorrectionField("mpin")}
                  label="MPIN"
                />
              )}
            </div>
            <div className="profile-bento-value">
              <CorrectionFieldDualCompare fieldKey="mpin">
                {serviceman.mpin != null && String(serviceman.mpin).trim() !== "" ? "Configured" : "Not set"}
              </CorrectionFieldDualCompare>
            </div>
          </article>
        </div>
      </div>

      {/* Primary Profession Section */}
      <div className="profession-section primary-profession">
        <h3 className="section-title">Primary Profession</h3>
        <div className="profile-container profile-bento-wrap">
          <div className="profile-bento-grid">
            {isSingleProfession() && serviceman.profession && (
              <BentoProfileField
                fieldKey="profession"
                label="Profession"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.profession}
              </BentoProfileField>
            )}
            {!isSingleProfession() && serviceman.professions && serviceman.professions.length > 0 && (
              <BentoProfileField
                fieldKey="professions"
                label="Primary profession"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {displayTextValue(serviceman.professions[0] || serviceman.profession)}
              </BentoProfileField>
            )}
                  {serviceman.professions && serviceman.professions.length > 1 && (
              <BentoProfileField
                fieldKey="professions"
                label="Registered professions"
                wide
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {normalizeTextArray(serviceman.professions).join(", ") || "N/A"}
              </BentoProfileField>
            )}
            <BentoProfileField
              fieldKey="description"
              label="Description"
              wide
              valueClassName="profile-bento-value--multiline"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.description || "N/A"}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="availabilityStart"
              label="Availability start"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {formatAvailabilityTime(serviceman.availabilityStart)}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="availabilityEnd"
              label="Availability end"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {formatAvailabilityTime(serviceman.availabilityEnd)}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="services"
              label="Services"
              wide
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.services?.length > 0 ? (
                <ul className="services-list">
                  {serviceman.services.map((service, index) => (
                    <li key={index}>{displayTextValue(service)}</li>
                  ))}
                </ul>
              ) : (
                "No services listed"
              )}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="subServices"
              label="Sub services"
              wide
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.subServices && serviceman.subServices.length > 0 ? (
                <ul className="services-list">
                  {serviceman.subServices.map((subService, index) => (
                    <li key={index}>{displayTextValue(subService)}</li>
                  ))}
                </ul>
              ) : (
                "N/A"
              )}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="avgRating"
              label="Average rating"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.avgRating ? `${serviceman.avgRating}/5` : "N/A"}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="reviewCount"
              label="Review count"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.reviewCount ?? 0}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="experienceRange"
              label="Experience range (years)"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.experienceRange && String(serviceman.experienceRange).trim() !== ""
                ? serviceman.experienceRange
                : "N/A"}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="teamSize"
              label="Team size"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {formatTeamSizeDisplay(serviceman.teamSize) || "N/A"}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="minimalVisitCharge"
              label="Minimal visit charge"
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.minimalVisitCharge !== undefined && serviceman.minimalVisitCharge !== null
                ? `₹${Number(serviceman.minimalVisitCharge).toLocaleString("en-IN")}`
                : "N/A"}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="minimalVisitIncludes"
              label="Minimal visit includes"
              wide
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.minimalVisitIncludes && serviceman.minimalVisitIncludes.length > 0 ? (
                <ul className="services-list">
                  {serviceman.minimalVisitIncludes.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                "N/A"
              )}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="fullServicePackage"
              label="Full service package"
              wide
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {primaryFsp ? (
                <>
                  {primaryFsp.price !== undefined && primaryFsp.price !== null && (
                    <div style={{ marginBottom: "0.5rem" }}>
                      <strong>Price:</strong> ₹{Number(primaryFsp.price).toLocaleString("en-IN")}
                    </div>
                  )}
                  {primaryFsp.includes.length > 0 ? (
                    <ul className="services-list">
                      {primaryFsp.includes.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <span>No includes listed</span>
                  )}
                </>
              ) : (
                "N/A"
              )}
            </BentoProfileField>
            <BentoProfileField
              fieldKey="brandCertification"
              label="Brand certification"
              wide
              correctionFields={correctionFields}
              canEdit={canEditServiceman}
              toggleField={toggleCorrectionField}
            >
              {serviceman.brandCertification && serviceman.brandCertification.url
                ? renderStorageFileLink(serviceman.brandCertification, "View brand certification")
                : "N/A"}
            </BentoProfileField>
          </div>
        </div>

        {/* Primary Profession Meta-Data */}
        {(serviceman.gender || serviceman.vehicleTypes?.length > 0 || vehicleTypeData ||
          serviceman.brandExpertise?.length > 0 || serviceman.engineCC?.length > 0 ||
          serviceman.fuelType?.length > 0 || serviceman.workshopType?.length > 0) && (
            <div className="profile-container profile-bento-wrap" style={{ marginTop: "1.5rem" }}>
              <h4 className="subsection-title">Primary Profession Meta-Data</h4>
              <div className="profile-bento-grid">
                {serviceman.gender && (
                  <BentoProfileField
                    fieldKey="gender"
                    label="Gender"
                    correctionFields={correctionFields}
                    canEdit={canEditServiceman}
                    toggleField={toggleCorrectionField}
                  >
                    {serviceman.gender}
                  </BentoProfileField>
                )}
                {serviceman.vehicleTypes && serviceman.vehicleTypes.length > 0 && (
                  <BentoProfileField
                    fieldKey="vehicleTypes"
                    label="Vehicle types"
                    wide
                    correctionFields={correctionFields}
                    canEdit={canEditServiceman}
                    toggleField={toggleCorrectionField}
                  >
                    <ul className="services-list">
                      {serviceman.vehicleTypes.map((vt, index) => (
                        <li key={index}>{displayTextValue(vt)}</li>
                      ))}
                    </ul>
                  </BentoProfileField>
                )}
                {vehicleTypeData && vehicleTypeData.length > 0 && (
                  <BentoProfileField
                    fieldKey="vehicleTypeData"
                    label="Vehicle type data"
                    wide
                    correctionFields={correctionFields}
                    canEdit={canEditServiceman}
                    toggleField={toggleCorrectionField}
                  >
                    <div>
                      {vehicleTypeData.map((vt, index) => (
                        <div key={index} className="vehicle-type-card">
                          <h4 className="vehicle-type-title">{displayTextValue(vt.vehicleType)}</h4>
                          {vt.services.length > 0 && (
                            <div className="vehicle-type-field">
                              <strong>Services:</strong>
                              <ul className="services-list">
                                {vt.services.map((service, i) => (
                                  <li key={i}>{displayTextValue(service)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {vt.subServices.length > 0 && (
                            <div className="vehicle-type-field">
                              <strong>Sub Services:</strong>
                              <ul className="services-list">
                                {vt.subServices.map((subService, i) => (
                                  <li key={i}>{displayTextValue(subService)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {vt.brandExpertise.length > 0 && (
                            <div className="vehicle-type-field">
                              <strong>Brand Expertise:</strong>
                              <ul className="services-list">
                                {vt.brandExpertise.map((brand, i) => (
                                  <li key={i}>{displayTextValue(brand)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {vt.engineCC.length > 0 && (
                            <div className="vehicle-type-field">
                              <strong>Engine CC:</strong>
                              <ul className="services-list">
                                {vt.engineCC.map((cc, i) => (
                                  <li key={i}>{displayTextValue(cc)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {vt.fuelType.length > 0 && (
                            <div className="vehicle-type-field">
                              <strong>Fuel Type:</strong>
                              <ul className="services-list">
                                {vt.fuelType.map((fuel, i) => (
                                  <li key={i}>{displayTextValue(fuel)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {vt.workshopType.length > 0 && (
                            <div className="vehicle-type-field">
                              <strong>Workshop Type:</strong>
                              <ul className="services-list">
                                {vt.workshopType.map((workshop, i) => (
                                  <li key={i}>{displayTextValue(workshop)}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </BentoProfileField>
                )}
                {serviceman.brandExpertise && serviceman.brandExpertise.length > 0 && (
                  <BentoProfileField
                    fieldKey="brandExpertise"
                    label="Brand expertise"
                    wide
                    correctionFields={correctionFields}
                    canEdit={canEditServiceman}
                    toggleField={toggleCorrectionField}
                  >
                    <ul className="services-list">
                      {serviceman.brandExpertise.map((brand, index) => (
                        <li key={index}>{displayTextValue(brand)}</li>
                      ))}
                    </ul>
                  </BentoProfileField>
                )}
                {serviceman.engineCC && serviceman.engineCC.length > 0 && (
                  <BentoProfileField
                    fieldKey="engineCC"
                    label="Engine CC"
                    wide
                    correctionFields={correctionFields}
                    canEdit={canEditServiceman}
                    toggleField={toggleCorrectionField}
                  >
                    <ul className="services-list">
                      {serviceman.engineCC.map((cc, index) => (
                        <li key={index}>{displayTextValue(cc)}</li>
                      ))}
                    </ul>
                  </BentoProfileField>
                )}
                {serviceman.fuelType && serviceman.fuelType.length > 0 && (
                  <BentoProfileField
                    fieldKey="fuelType"
                    label="Fuel type"
                    wide
                    correctionFields={correctionFields}
                    canEdit={canEditServiceman}
                    toggleField={toggleCorrectionField}
                  >
                    <ul className="services-list">
                      {serviceman.fuelType.map((fuel, index) => (
                        <li key={index}>{displayTextValue(fuel)}</li>
                      ))}
                    </ul>
                  </BentoProfileField>
                )}
                {serviceman.workshopType && serviceman.workshopType.length > 0 && (
                  <BentoProfileField
                    fieldKey="workshopType"
                    label="Workshop type"
                    wide
                    correctionFields={correctionFields}
                    canEdit={canEditServiceman}
                    toggleField={toggleCorrectionField}
                  >
                    <ul className="services-list">
                      {serviceman.workshopType.map((workshop, index) => (
                        <li key={index}>{displayTextValue(workshop)}</li>
                      ))}
                    </ul>
                  </BentoProfileField>
                )}
              </div>
            </div>
          )}

        {/* Primary Profession Media */}
        {serviceman.media && serviceman.media.filter(item => item && typeof item === 'object' && item.url).length > 0 && (
          <ProfileSubsectionWithCorrection
            title="Primary Profession Media"
            fieldKey="media"
            canEdit={canEditServiceman}
            correctionFields={correctionFields}
            toggleField={toggleCorrectionField}
          >
            <div className="media-grid">
              {serviceman.media.map((item, index) => {
                // Skip empty strings (hidden items)
                if (!item || item === "" || (typeof item === 'object' && !item.url)) {
                  return null;
                }
                return (
                  <div key={index} className="media-item-container">
                    {item.type === "image" ? (
                      <img
                        src={item.url}
                        alt={`Media ${index + 1}`}
                        className="media-item clickable-media"
                        onClick={() => handleMediaClick(item, index)}
                        onError={(e) => {
                          e.target.src = DEFAULT_IMAGE;
                        }}
                      />
                    ) : (
                      <video
                        src={item.url}
                        controls
                        className="media-item clickable-media"
                        onClick={() => handleMediaClick(item, index)}
                        onError={(e) => {
                          console.error("Video load error:", e);
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </ProfileSubsectionWithCorrection>
        )}

        {/* Hidden Media Section */}
        {(serviceman.hidden && serviceman.hidden.length > 0) || (hiddenMedia && hiddenMedia.length > 0) ? (
          <ProfileSubsectionWithCorrection
            title="Hidden Media"
            fieldKey="hidden"
            canEdit={canEditServiceman}
            correctionFields={correctionFields}
            toggleField={toggleCorrectionField}
          >
            <div className="media-grid">
              {(serviceman.hidden || hiddenMedia || []).map((item, index) => {
                if (!item || !item.url) return null;
                return (
                  <div key={index} className="media-item-container">
                    {item.type === "image" ? (
                      <img
                        src={item.url}
                        alt={`Hidden Media ${index + 1}`}
                        className="media-item clickable-media"
                        onClick={() => handleMediaClick(item, index)}
                        onError={(e) => {
                          e.target.src = DEFAULT_IMAGE;
                        }}
                        style={{ opacity: 0.7 }}
                      />
                    ) : (
                      <video
                        src={item.url}
                        controls
                        className="media-item clickable-media"
                        onClick={() => handleMediaClick(item, index)}
                        onError={(e) => {
                          console.error("Video load error:", e);
                        }}
                        style={{ opacity: 0.7 }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </ProfileSubsectionWithCorrection>
        ) : null}

        {/* Hidden Documents Section */}
        {(serviceman.hiddenDocuments && serviceman.hiddenDocuments.length > 0) || (hiddenDocuments && hiddenDocuments.length > 0) ? (
          <ProfileSubsectionWithCorrection
            title="Hidden Documents"
            fieldKey="hiddenDocuments"
            canEdit={canEditServiceman}
            correctionFields={correctionFields}
            toggleField={toggleCorrectionField}
          >
            <ul className="documents-list">
              {(serviceman.hiddenDocuments || hiddenDocuments || []).map((doc, index) => {
                if (!doc || (!doc?.file?.url && !doc?.url)) return null;

                const fileUrl = doc?.file?.url || doc?.url || "";
                const fileName =
                  doc?.file?.name || doc?.name || `Document ${index + 1}`;
                const rawType = (doc?.file?.type || doc?.type || "unknown").toLowerCase();
                const lowerName = fileName.toLowerCase();

                let displayType = "other";
                if (rawType.includes("pdf") || lowerName.endsWith(".pdf")) {
                  displayType = "pdf";
                } else if (
                  rawType.includes("image") ||
                  rawType.startsWith("image/") ||
                  [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
                    lowerName.endsWith(ext)
                  )
                ) {
                  displayType = "image";
                } else if (
                  rawType.includes("word") ||
                  [".doc", ".docx"].some((ext) => lowerName.endsWith(ext))
                ) {
                  displayType = "word";
                }

                const title =
                  doc?.title && doc.title.trim() !== ""
                    ? doc.title
                    : doc?.file?.title && doc.file.title.trim() !== ""
                      ? doc.file.title
                      : fileName;

                const typeLabel =
                  displayType === "other"
                    ? rawType.toUpperCase()
                    : displayType.toUpperCase();

                return (
                  <li key={index} className="document-item" style={{ opacity: 0.7 }}>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="document-link"
                    >
                      <div className="document-thumbnail-wrapper">
                        {displayType === "image" ? (
                          <img
                            src={fileUrl}
                            alt={title}
                            className="document-thumbnail-image"
                          />
                        ) : (
                          <div
                            className={`document-thumbnail-icon document-thumbnail-icon-${displayType}`}
                          >
                            {displayType === "pdf" && "PDF"}
                            {displayType === "word" && "WORD"}
                            {displayType === "other" && "FILE"}
                          </div>
                        )}
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
          </ProfileSubsectionWithCorrection>
        ) : null}

        {/* Primary Profession Documents */}
        {serviceman.documents && serviceman.documents.length > 0 && (
          <ProfileSubsectionWithCorrection
            title="Primary Profession Documents"
            fieldKey="documents"
            canEdit={canEditServiceman}
            correctionFields={correctionFields}
            toggleField={toggleCorrectionField}
          >
            <ul className="documents-list">
              {serviceman.documents.map((doc, index) => {
                const fileUrl = doc?.file?.url || doc?.url || "";
                const fileName =
                  doc?.file?.name || doc?.name || `Document ${index + 1}`;
                const rawType = (doc?.file?.type || doc?.type || "unknown").toLowerCase();
                const lowerName = fileName.toLowerCase();

                let displayType = "other";
                if (rawType.includes("pdf") || lowerName.endsWith(".pdf")) {
                  displayType = "pdf";
                } else if (
                  rawType.includes("image") ||
                  rawType.startsWith("image/") ||
                  [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
                    lowerName.endsWith(ext)
                  )
                ) {
                  displayType = "image";
                } else if (
                  rawType.includes("word") ||
                  [".doc", ".docx"].some((ext) => lowerName.endsWith(ext))
                ) {
                  displayType = "word";
                }

                const title =
                  doc?.title && doc.title.trim() !== ""
                    ? doc.title
                    : doc?.file?.title && doc.file.title.trim() !== ""
                      ? doc.file.title
                      : fileName;

                const typeLabel =
                  displayType === "other"
                    ? rawType.toUpperCase()
                    : displayType.toUpperCase();

                return (
                  <li key={index} className="document-item">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="document-link"
                    >
                      <div className="document-thumbnail-wrapper">
                        {displayType === "image" ? (
                          <img
                            src={fileUrl}
                            alt={title}
                            className="document-thumbnail-image"
                          />
                        ) : (
                          <div
                            className={`document-thumbnail-icon document-thumbnail-icon-${displayType}`}
                          >
                            {displayType === "pdf" && "PDF"}
                            {displayType === "word" && "WORD"}
                            {displayType === "other" && "FILE"}
                          </div>
                        )}
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
          </ProfileSubsectionWithCorrection>
        )}
      </div>

      {/* Secondary Profession Section - Only shown when secondary profession exists */}
      {hasSecondaryProfession() && (
        <div className="profession-section secondary-profession">
          <h3 className="section-title">Secondary Profession</h3>
          <div className="profile-container profile-bento-wrap">
            <div className="profile-bento-grid">
              <BentoProfileField
                fieldKey="secondaryProfession"
                label="Secondary profession"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {normalizeProfessionValue(serviceman.secondaryProfession) || "N/A"}
              </BentoProfileField>
              {serviceman.professions && serviceman.professions.length > 1 && (
                <BentoProfileField
                  fieldKey="professionsSecondary"
                  label="Secondary profession (from list)"
                  correctionFields={correctionFields}
                  canEdit={canEditServiceman}
                  toggleField={toggleCorrectionField}
                >
                  {displayTextValue(serviceman.professions[1])}
                </BentoProfileField>
              )}
              <BentoProfileField
                fieldKey="enableSecondaryProfession"
                label="Secondary profession enabled"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.enableSecondaryProfession !== undefined
                  ? serviceman.enableSecondaryProfession
                    ? "Yes"
                    : "No"
                  : "N/A"}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryDescription"
                label="Description"
                wide
                valueClassName="profile-bento-value--multiline"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.secondaryDescription || "N/A"}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryAreaName"
                label="Secondary area"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.secondaryAreaName || "N/A"}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryAvailabilityStart"
                label="Secondary availability start"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {formatAvailabilityTime(serviceman.secondaryAvailabilityStart)}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryAvailabilityEnd"
                label="Secondary availability end"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {formatAvailabilityTime(serviceman.secondaryAvailabilityEnd)}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryServices"
                label="Secondary services"
                wide
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.secondaryServices && serviceman.secondaryServices.length > 0 ? (
                  <ul className="services-list">
                    {serviceman.secondaryServices.map((service, index) => (
                      <li key={index}>{displayTextValue(service)}</li>
                    ))}
                  </ul>
                ) : (
                  "N/A"
                )}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondarySubServices"
                label="Secondary sub services"
                wide
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.secondarySubServices && serviceman.secondarySubServices.length > 0 ? (
                  <ul className="services-list">
                    {serviceman.secondarySubServices.map((subService, index) => (
                      <li key={index}>{displayTextValue(subService)}</li>
                    ))}
                  </ul>
                ) : (
                  "N/A"
                )}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryExperienceRange"
                label="Secondary experience range (years)"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.secondaryExperienceRange && String(serviceman.secondaryExperienceRange).trim() !== ""
                  ? serviceman.secondaryExperienceRange
                  : "N/A"}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryTeamSize"
                label="Secondary team size"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {formatTeamSizeDisplay(serviceman.secondaryTeamSize) || "N/A"}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryMinimalVisitCharge"
                label="Secondary minimal visit charge"
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.secondaryMinimalVisitCharge !== undefined && serviceman.secondaryMinimalVisitCharge !== null
                  ? `₹${Number(serviceman.secondaryMinimalVisitCharge).toLocaleString("en-IN")}`
                  : "N/A"}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryMinimalVisitIncludes"
                label="Secondary minimal visit includes"
                wide
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {serviceman.secondaryMinimalVisitIncludes && serviceman.secondaryMinimalVisitIncludes.length > 0 ? (
                  <ul className="services-list">
                    {serviceman.secondaryMinimalVisitIncludes.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  "N/A"
                )}
              </BentoProfileField>
              <BentoProfileField
                fieldKey="secondaryFullServicePackage"
                label="Secondary full service package"
                wide
                correctionFields={correctionFields}
                canEdit={canEditServiceman}
                toggleField={toggleCorrectionField}
              >
                {secondaryFsp ? (
                  <>
                    {secondaryFsp.price !== undefined && secondaryFsp.price !== null && (
                      <div style={{ marginBottom: "0.5rem" }}>
                        <strong>Price:</strong> ₹{Number(secondaryFsp.price).toLocaleString("en-IN")}
                      </div>
                    )}
                    {secondaryFsp.includes.length > 0 ? (
                      <ul className="services-list">
                        {secondaryFsp.includes.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <span>No includes listed</span>
                    )}
                  </>
                ) : (
                  "N/A"
                )}
              </BentoProfileField>
            </div>
          </div>

          {/* Secondary Profession Meta-Data */}
          {(secondaryVehicleTypeData || serviceman.secondaryBrandExpertise?.length > 0 ||
            serviceman.secondaryEngineCC?.length > 0 || serviceman.secondaryFuelType?.length > 0 ||
            serviceman.secondaryWorkshopType?.length > 0) && (
              <div className="profile-container profile-bento-wrap" style={{ marginTop: "1.5rem" }}>
                <h4 className="subsection-title">Secondary Profession Meta-Data</h4>
                <div className="profile-bento-grid">
                  {secondaryVehicleTypeData && secondaryVehicleTypeData.length > 0 && (
                    <BentoProfileField
                      fieldKey="secondaryVehicleTypeData"
                      label="Secondary vehicle type data"
                      wide
                      correctionFields={correctionFields}
                      canEdit={canEditServiceman}
                      toggleField={toggleCorrectionField}
                    >
                      <div>
                        {secondaryVehicleTypeData.map((vt, index) => (
                          <div key={index} className="vehicle-type-card">
                            <h4 className="vehicle-type-title">{displayTextValue(vt.vehicleType)}</h4>
                            {vt.services.length > 0 && (
                              <div className="vehicle-type-field">
                                <strong>Services:</strong>
                                <ul className="services-list">
                                  {vt.services.map((service, i) => (
                                    <li key={i}>{displayTextValue(service)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {vt.subServices.length > 0 && (
                              <div className="vehicle-type-field">
                                <strong>Sub Services:</strong>
                                <ul className="services-list">
                                  {vt.subServices.map((subService, i) => (
                                    <li key={i}>{displayTextValue(subService)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {vt.brandExpertise.length > 0 && (
                              <div className="vehicle-type-field">
                                <strong>Brand Expertise:</strong>
                                <ul className="services-list">
                                  {vt.brandExpertise.map((brand, i) => (
                                    <li key={i}>{displayTextValue(brand)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {vt.engineCC.length > 0 && (
                              <div className="vehicle-type-field">
                                <strong>Engine CC:</strong>
                                <ul className="services-list">
                                  {vt.engineCC.map((cc, i) => (
                                    <li key={i}>{displayTextValue(cc)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {vt.fuelType.length > 0 && (
                              <div className="vehicle-type-field">
                                <strong>Fuel Type:</strong>
                                <ul className="services-list">
                                  {vt.fuelType.map((fuel, i) => (
                                    <li key={i}>{displayTextValue(fuel)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {vt.workshopType.length > 0 && (
                              <div className="vehicle-type-field">
                                <strong>Workshop Type:</strong>
                                <ul className="services-list">
                                  {vt.workshopType.map((workshop, i) => (
                                    <li key={i}>{displayTextValue(workshop)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </BentoProfileField>
                  )}
                  {serviceman.secondaryBrandExpertise && serviceman.secondaryBrandExpertise.length > 0 && (
                    <BentoProfileField
                      fieldKey="secondaryBrandExpertise"
                      label="Secondary brand expertise"
                      wide
                      correctionFields={correctionFields}
                      canEdit={canEditServiceman}
                      toggleField={toggleCorrectionField}
                    >
                      <ul className="services-list">
                        {serviceman.secondaryBrandExpertise.map((brand, index) => (
                          <li key={index}>{displayTextValue(brand)}</li>
                        ))}
                      </ul>
                    </BentoProfileField>
                  )}
                  {serviceman.secondaryEngineCC && serviceman.secondaryEngineCC.length > 0 && (
                    <BentoProfileField
                      fieldKey="secondaryEngineCC"
                      label="Secondary engine CC"
                      wide
                      correctionFields={correctionFields}
                      canEdit={canEditServiceman}
                      toggleField={toggleCorrectionField}
                    >
                      <ul className="services-list">
                        {serviceman.secondaryEngineCC.map((cc, index) => (
                          <li key={index}>{displayTextValue(cc)}</li>
                        ))}
                      </ul>
                    </BentoProfileField>
                  )}
                  {serviceman.secondaryFuelType && serviceman.secondaryFuelType.length > 0 && (
                    <BentoProfileField
                      fieldKey="secondaryFuelType"
                      label="Secondary fuel type"
                      wide
                      correctionFields={correctionFields}
                      canEdit={canEditServiceman}
                      toggleField={toggleCorrectionField}
                    >
                      <ul className="services-list">
                        {serviceman.secondaryFuelType.map((fuel, index) => (
                          <li key={index}>{displayTextValue(fuel)}</li>
                        ))}
                      </ul>
                    </BentoProfileField>
                  )}
                  {serviceman.secondaryWorkshopType && serviceman.secondaryWorkshopType.length > 0 && (
                    <BentoProfileField
                      fieldKey="secondaryWorkshopType"
                      label="Secondary workshop type"
                      wide
                      correctionFields={correctionFields}
                      canEdit={canEditServiceman}
                      toggleField={toggleCorrectionField}
                    >
                      <ul className="services-list">
                        {serviceman.secondaryWorkshopType.map((workshop, index) => (
                          <li key={index}>{displayTextValue(workshop)}</li>
                        ))}
                      </ul>
                    </BentoProfileField>
                  )}
                </div>
              </div>
            )}

          {/* Secondary Profession Media */}
          {(() => {
            const secondaryMedia = serviceman.secondaryMedia;

            if (!secondaryMedia || !Array.isArray(secondaryMedia) || secondaryMedia.length === 0) {
              return null;
            }

            return (
              <ProfileSubsectionWithCorrection
                title="Secondary Profession Media"
                fieldKey="secondaryMedia"
                canEdit={canEditServiceman}
                correctionFields={correctionFields}
                toggleField={toggleCorrectionField}
              >
                <div className="media-grid">
                  {secondaryMedia.map((item, index) => {

                    if (!item || !item.url) {
                      console.error(`Item ${index} is missing url:`, item);
                      return (
                        <div key={index} className="media-item-container">
                          <img
                            src={DEFAULT_IMAGE}
                            alt={`Secondary Media ${index + 1} - Missing URL`}
                            className="media-item"
                          />
                        </div>
                      );
                    }

                    return (
                      <div key={index} className="media-item-container">
                        {item.type === "image" ? (
                          <img
                            src={item.url}
                            alt={`Secondary Media ${index + 1}`}
                            className="media-item clickable-media"
                            onClick={() => handleMediaClick(item, index, false, 'secondary')}
                            onError={(e) => {
                              console.error(`Image ${index} failed to load:`, item.url);
                              console.error("Error event:", e);
                              e.target.src = DEFAULT_IMAGE;
                            }}
                            onLoad={() => {
                            }}
                          />
                        ) : (
                          <video
                            src={item.url}
                            controls
                            className="media-item clickable-media"
                            onClick={() => handleMediaClick(item, index, false, 'secondary')}
                            onError={(e) => {
                              console.error("Video load error:", e);
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </ProfileSubsectionWithCorrection>
            );
          })()}

          {/* Secondary Profession Documents */}
          {serviceman.secondaryDocuments && serviceman.secondaryDocuments.length > 0 && (
            <ProfileSubsectionWithCorrection
              title="Secondary Profession Documents"
              fieldKey="secondaryDocuments"
              canEdit={canEditServiceman}
              correctionFields={correctionFields}
              toggleField={toggleCorrectionField}
            >
              <ul className="documents-list">
                {serviceman.secondaryDocuments.map((doc, index) => {
                  const fileUrl = doc?.file?.url || doc?.url || "";
                  const fileName =
                    doc?.file?.name || doc?.name || `Secondary Document ${index + 1}`;
                  const rawType = (doc?.file?.type || doc?.type || "unknown").toLowerCase();
                  const lowerName = fileName.toLowerCase();

                  let displayType = "other";
                  if (rawType.includes("pdf") || lowerName.endsWith(".pdf")) {
                    displayType = "pdf";
                  } else if (
                    rawType.includes("image") ||
                    rawType.startsWith("image/") ||
                    [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
                      lowerName.endsWith(ext)
                    )
                  ) {
                    displayType = "image";
                  } else if (
                    rawType.includes("word") ||
                    [".doc", ".docx"].some((ext) => lowerName.endsWith(ext))
                  ) {
                    displayType = "word";
                  }

                  const title =
                    doc?.title && doc.title.trim() !== ""
                      ? doc.title
                      : doc?.file?.title && doc.file.title.trim() !== ""
                        ? doc.file.title
                        : fileName;

                  const typeLabel =
                    displayType === "other"
                      ? rawType.toUpperCase()
                      : displayType.toUpperCase();

                  return (
                    <li key={index} className="document-item">
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-link"
                      >
                        <div className="document-thumbnail-wrapper">
                          {displayType === "image" ? (
                            <img
                              src={fileUrl}
                              alt={title}
                              className="document-thumbnail-image"
                            />
                          ) : (
                            <div
                              className={`document-thumbnail-icon document-thumbnail-icon-${displayType}`}
                            >
                              {displayType === "pdf" && "PDF"}
                              {displayType === "word" && "WORD"}
                              {displayType === "other" && "FILE"}
                            </div>
                          )}
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </ProfileSubsectionWithCorrection>
          )}

          {/* Hidden Secondary Media Section */}
          {(serviceman.hiddenSecondary && serviceman.hiddenSecondary.length > 0) || (hiddenSecondaryMedia && hiddenSecondaryMedia.length > 0) ? (
            <ProfileSubsectionWithCorrection
              title="Hidden Secondary Media"
              fieldKey="hiddenSecondary"
              canEdit={canEditServiceman}
              correctionFields={correctionFields}
              toggleField={toggleCorrectionField}
            >
              <div className="media-grid">
                {(serviceman.hiddenSecondary || hiddenSecondaryMedia || []).map((item, index) => {
                  if (!item || !item.url) return null;
                  return (
                    <div key={index} className="media-item-container">
                      {item.type === "image" ? (
                        <img
                          src={item.url}
                          alt={`Hidden Secondary Media ${index + 1}`}
                          className="media-item clickable-media"
                          onClick={() => handleMediaClick(item, index, false, 'secondary')}
                          onError={(e) => {
                            e.target.src = DEFAULT_IMAGE;
                          }}
                        />
                      ) : (
                        <video
                          src={item.url}
                          controls
                          className="media-item clickable-media"
                          onClick={() => handleMediaClick(item, index, false, 'secondary')}
                          onError={(e) => {
                            console.error("Video load error:", e);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </ProfileSubsectionWithCorrection>
          ) : null}

          {/* Hidden Secondary Documents Section */}
          {(serviceman.hiddenSecondaryDocuments && serviceman.hiddenSecondaryDocuments.length > 0) || (hiddenSecondaryDocuments && hiddenSecondaryDocuments.length > 0) ? (
            <ProfileSubsectionWithCorrection
              title="Hidden Secondary Documents"
              fieldKey="hiddenSecondaryDocuments"
              canEdit={canEditServiceman}
              correctionFields={correctionFields}
              toggleField={toggleCorrectionField}
            >
              <ul className="documents-list">
                {(serviceman.hiddenSecondaryDocuments || hiddenSecondaryDocuments || []).map((doc, index) => {
                  if (!doc || (!doc?.file?.url && !doc?.url)) return null;

                  const fileUrl = doc?.file?.url || doc?.url || "";
                  const fileName =
                    doc?.file?.name || doc?.name || `Secondary Document ${index + 1}`;
                  const rawType = (doc?.file?.type || doc?.type || "unknown").toLowerCase();
                  const lowerName = fileName.toLowerCase();

                  let displayType = "other";
                  if (rawType.includes("pdf") || lowerName.endsWith(".pdf")) {
                    displayType = "pdf";
                  } else if (
                    rawType.includes("image") ||
                    rawType.startsWith("image/") ||
                    [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
                      lowerName.endsWith(ext)
                    )
                  ) {
                    displayType = "image";
                  } else if (
                    rawType.includes("word") ||
                    [".doc", ".docx"].some((ext) => lowerName.endsWith(ext))
                  ) {
                    displayType = "word";
                  }

                  const title =
                    doc?.title && doc.title.trim() !== ""
                      ? doc.title
                      : doc?.file?.title && doc.file.title.trim() !== ""
                        ? doc.file.title
                        : fileName;

                  const typeLabel =
                    displayType === "other"
                      ? rawType.toUpperCase()
                      : displayType.toUpperCase();

                  return (
                    <li key={index} className="document-item" style={{ opacity: 0.7 }}>
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-link"
                      >
                        <div className="document-thumbnail-wrapper">
                          {displayType === "image" ? (
                            <img
                              src={fileUrl}
                              alt={title}
                              className="document-thumbnail-image"
                            />
                          ) : (
                            <div
                              className={`document-thumbnail-icon document-thumbnail-icon-${displayType}`}
                            >
                              {displayType === "pdf" && "PDF"}
                              {displayType === "word" && "WORD"}
                              {displayType === "other" && "FILE"}
                            </div>
                          )}
                        </div>
                        {/* No filename text for hidden docs */}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </ProfileSubsectionWithCorrection>
          ) : null}
        </div>
      )}

      {/* Profile Picture Fullscreen Modal */}
      {showProfilePictureFullscreen && (
        <div 
          className="profile-picture-fullscreen-overlay" 
          onClick={() => setShowProfilePictureFullscreen(false)}
        >
          <div 
            className="profile-picture-fullscreen-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="close-profile-picture-fullscreen" 
              onClick={() => setShowProfilePictureFullscreen(false)}
            >
              ×
            </button>
            <img
              src={imageSrc}
              alt="Profile Picture Fullscreen"
              className="profile-picture-fullscreen-image"
              onError={(e) => {
                e.target.src = DEFAULT_IMAGE;
              }}
            />
          </div>
        </div>
      )}

      {/* Media Viewer Modal */}
      {selectedMedia && (
        <div className="media-viewer-overlay" onClick={handleCloseMediaViewer}>
          <div className="media-viewer-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-media-viewer" onClick={handleCloseMediaViewer}>
              ×
            </button>
            {(() => {
              const currentMediaArray = selectedMediaType === 'secondary'
                ? (serviceman?.secondaryMedia || [])
                : media;
              const totalMedia = currentMediaArray.length;
              return (
                <>
                  {selectedMediaIndex > 0 && (
                    <button className="media-nav-btn prev-btn" onClick={handlePreviousMedia}>
                      ‹
                    </button>
                  )}
                  {selectedMedia.type === "image" ? (
                    <img
                      src={selectedMedia.url}
                      alt="Full screen media"
                      className="fullscreen-media"
                    />
                  ) : (
                    <video
                      src={selectedMedia.url}
                      controls
                      autoPlay
                      className="fullscreen-media"
                    />
                  )}
                  {selectedMediaIndex >= 0 && selectedMediaIndex < totalMedia - 1 && (
                    <button className="media-nav-btn next-btn" onClick={handleNextMedia}>
                      ›
                    </button>
                  )}
                  <div className="media-viewer-info">
                    {selectedMediaIndex >= 0 && (
                      <span>
                        {selectedMediaIndex + 1} / {totalMedia}
                      </span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditing && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Serviceman Profile</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="e.g., +1234567890"
                  disabled={true}
                />
              </div>
              <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  id="isVerified"
                  name="isVerified"
                  checked={formData.isVerified}
                  onChange={(e) => setFormData(prev => ({ ...prev, isVerified: e.target.checked }))}
                  style={{ width: "auto" }}
                />
                <label htmlFor="isVerified" style={{ marginBottom: 0 }}>Verified Status (Check to mark as verified)</label>
              </div>
              <div className="form-group">
                <label htmlFor="profession">Profession</label>
                <select
                  id="profession"
                  name="profession"
                  value={formData.profession}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a profession</option>
                  {professionOptions.map((profession) => (
                    <option key={profession.value} value={profession.value}>
                      {profession.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Services</label>
                {formData.profession ? (
                  <div className="services-checkboxes" ref={servicesContainerRef} style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {getServicesForProfession(formData.profession).map((service) => {
                      const serviceValue = service.value || service.label || service;
                      const serviceLabel = service.label || service.value || service;
                      const hasSubServices = service.subServices && service.subServices.length > 0;
                      const subServices = hasSubServices ? getSubServicesForService(formData.profession, serviceValue) : [];
                      const isServiceSelected = (formData.services || []).includes(serviceValue) ||
                        (formData.services || []).includes(serviceLabel);

                      return (
                        <div key={serviceValue} className="checkbox-item" style={{ marginBottom: hasSubServices ? "15px" : "5px" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <input
                              type="checkbox"
                              id={`service-${serviceValue}`}
                              value={serviceValue}
                              checked={isServiceSelected}
                              onChange={() => handleServicesChange(serviceValue)}
                            />
                            <label htmlFor={`service-${serviceValue}`} style={{ marginLeft: "8px", fontWeight: hasSubServices ? "bold" : "normal" }}>
                              {serviceLabel}
                            </label>
                          </div>
                          {hasSubServices && isServiceSelected && (
                            <div style={{ marginLeft: "25px", marginTop: "8px", paddingLeft: "10px", borderLeft: "2px solid #3b82f6" }}>
                              <label style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px", display: "block" }}>Sub Services:</label>
                              {subServices.map((subService) => {
                                const subValue = subService.value || subService;
                                const subLabel = subService.label || subService.value || subService;
                                const isSubSelected = (formData.subServices || []).includes(subValue) ||
                                  (formData.subServices || []).includes(subLabel);
                                return (
                                  <div key={subValue} style={{ marginBottom: "5px" }}>
                                    <input
                                      type="checkbox"
                                      id={`subservice-${serviceValue}-${subValue}`}
                                      checked={isSubSelected}
                                      onChange={() => {
                                        const currentSubServices = formData.subServices || [];
                                        const newSubServices = isSubSelected
                                          ? currentSubServices.filter(s => s !== subValue && s !== subLabel)
                                          : [...currentSubServices, subValue];
                                        setFormData({ ...formData, subServices: newSubServices });
                                      }}
                                    />
                                    <label htmlFor={`subservice-${serviceValue}-${subValue}`} style={{ marginLeft: "8px", fontSize: "0.85em" }}>
                                      {subLabel}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: "#999", fontStyle: "italic" }}>Please select a profession first</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="profilePicture">Profile Picture</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <img src={serviceman.image || DEFAULT_IMAGE} alt="Current profile" style={{ width: "100px", height: "100px", borderRadius: "50%", marginBottom: "10px" }} />
                  {serviceman.image && serviceman.image !== DEFAULT_IMAGE && !isProfilePictureDeleted && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete the profile picture? It will be replaced with a default image.")) {
                          setIsProfilePictureDeleted(true);
                        }
                      }}
                      style={{ background: "red", color: "white", border: "none", padding: "8px 15px", borderRadius: "4px", cursor: "pointer", marginBottom: "10px" }}
                    >
                      Delete Profile Picture
                    </button>
                  )}
                  {isProfilePictureDeleted && (
                    <p style={{ color: "#ff6b6b", fontSize: "0.9em", marginBottom: "10px" }}>Profile picture will be deleted and replaced with default image on save.</p>
                  )}
                </div>
                <p style={{ fontSize: "0.9em", color: "#666", fontStyle: "italic" }}>Note: You can only delete the profile picture. It will be replaced with a default image.</p>
              </div>
              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {hasVehicleFields(formData.profession) && (() => {
                const vehicleFields = getVehicleFields(formData.profession);
                if (!vehicleFields) return null;
                return (
                  <>
                    {vehicleFields.brandExpertise.length > 0 && (
                      <div className="form-group">
                        <label>Brand Expertise</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.brandExpertise.map((brand) => {
                            const isSelected = (formData.brandExpertise || []).includes(brand);
                            return (
                              <div key={brand} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`brand-${brand}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.brandExpertise || [];
                                    const updated = isSelected
                                      ? current.filter(b => b !== brand)
                                      : [...current, brand];
                                    setFormData({ ...formData, brandExpertise: updated });
                                  }}
                                />
                                <label htmlFor={`brand-${brand}`} style={{ marginLeft: "8px" }}>{brand}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {vehicleFields.engineCC.length > 0 && (
                      <div className="form-group">
                        <label>Engine CC</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.engineCC.map((cc) => {
                            const isSelected = (formData.engineCC || []).includes(cc);
                            return (
                              <div key={cc} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`engineCC-${cc}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.engineCC || [];
                                    const updated = isSelected
                                      ? current.filter(c => c !== cc)
                                      : [...current, cc];
                                    setFormData({ ...formData, engineCC: updated });
                                  }}
                                />
                                <label htmlFor={`engineCC-${cc}`} style={{ marginLeft: "8px" }}>{cc}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {vehicleFields.fuelType.length > 0 && (
                      <div className="form-group">
                        <label>Fuel Type</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.fuelType.map((fuel) => {
                            const isSelected = (formData.fuelType || []).includes(fuel);
                            return (
                              <div key={fuel} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`fuel-${fuel}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.fuelType || [];
                                    const updated = isSelected
                                      ? current.filter(f => f !== fuel)
                                      : [...current, fuel];
                                    setFormData({ ...formData, fuelType: updated });
                                  }}
                                />
                                <label htmlFor={`fuel-${fuel}`} style={{ marginLeft: "8px" }}>{fuel}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {vehicleFields.workshopType.length > 0 && (
                      <div className="form-group">
                        <label>Workshop Type</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.workshopType.map((workshop) => {
                            const isSelected = (formData.workshopType || []).includes(workshop);
                            return (
                              <div key={workshop} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`workshop-${workshop}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.workshopType || [];
                                    const updated = isSelected
                                      ? current.filter(w => w !== workshop)
                                      : [...current, workshop];
                                    setFormData({ ...formData, workshopType: updated });
                                  }}
                                />
                                <label htmlFor={`workshop-${workshop}`} style={{ marginLeft: "8px" }}>{workshop}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {vehicleFields.vehicleTypes && Array.isArray(vehicleFields.vehicleTypes) && vehicleFields.vehicleTypes.length > 0 && (
                      <div className="form-group">
                        <label>Vehicle Types</label>
                        <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.vehicleTypes.map((vtCategory, catIndex) => (
                            <div key={catIndex} style={{ marginBottom: "15px", paddingBottom: "10px", borderBottom: "1px solid #eee" }}>
                              <strong style={{ display: "block", marginBottom: "8px", color: "#3b82f6" }}>{vtCategory.category}</strong>
                              {vtCategory.vehicles && vtCategory.vehicles.map((vehicle) => {
                                const vehicleValue = vehicle.value || vehicle.label;
                                const vehicleLabel = vehicle.label || vehicle.value;
                                const isSelected = (formData.vehicleTypes || []).includes(vehicleValue) ||
                                  (formData.vehicleTypes || []).includes(vehicleLabel);
                                return (
                                  <div key={vehicleValue} style={{ marginLeft: "15px", marginBottom: "8px" }}>
                                    <input
                                      type="checkbox"
                                      id={`vehicle-${vehicleValue}`}
                                      checked={isSelected}
                                      onChange={() => {
                                        const current = formData.vehicleTypes || [];
                                        const updated = isSelected
                                          ? current.filter(v => v !== vehicleValue && v !== vehicleLabel)
                                          : [...current, vehicleValue];
                                        setFormData({ ...formData, vehicleTypes: updated });
                                      }}
                                    />
                                    <label htmlFor={`vehicle-${vehicleValue}`} style={{ marginLeft: "8px", fontWeight: "500" }}>{vehicleLabel}</label>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="form-group">
                <label>Current Media</label>
                {media.filter(item => item && item.url).length > 0 ? (
                  <div className="media-grid edit-media-grid">
                    {media.map((item, index) => {
                      // Skip invalid items
                      if (!item || !item.url) {
                        return null;
                      }
                      return (
                        <div key={index} className="media-item-container">
                          {item.type === "image" ? (
                            <img
                              src={item.url}
                              alt={`Media ${index + 1}`}
                              className="media-item clickable-media"
                              onClick={() => handleMediaClick(item, index)}
                              onError={(e) => {
                                e.target.src = DEFAULT_IMAGE;
                              }}
                            />
                          ) : (
                            <video
                              src={item.url}
                              controls
                              className="media-item clickable-media"
                              onClick={() => handleMediaClick(item, index)}
                              onError={(e) => {
                                console.error("Video load error:", e);
                              }}
                            />
                          )}
                          <button
                            className="hide-media-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHideMedia(index);
                            }}
                            title="Hide media"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5Z"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <circle
                                cx="12"
                                cy="12.5"
                                r="3"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <button
                            className="remove-media-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMedia(index);
                            }}
                            title="Delete media"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5 7H19"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 10V16"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 10V16"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 7L7 18C7 19.1046 7.89543 20 9 20H15C16.1046 20 17 19.1046 17 18L18 7"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9 7V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-media">No media available</p>
                )}
              </div>
              <div className="form-group">
                <p style={{ fontSize: "0.9em", color: "#666", fontStyle: "italic" }}>Note: You can only delete existing media files. New files cannot be uploaded from the admin panel.</p>
              </div>
              <div className="form-group">
                <label>Current Documents</label>
                {documents.filter(item => item).length > 0 ? (
                  <div className="documents-list">
                    {documents.map((doc, index) => {
                      // Skip invalid items
                      if (!doc) {
                        return null;
                      }

                      const fileUrl = doc?.file?.url || doc?.url || "";
                      const fileName =
                        doc?.file?.name || doc?.name || `Document ${index + 1}`;
                      const rawType = (doc?.file?.type || doc?.type || "unknown").toLowerCase();
                      const lowerName = fileName.toLowerCase();

                      let displayType = "other";
                      if (rawType.includes("pdf") || lowerName.endsWith(".pdf")) {
                        displayType = "pdf";
                      } else if (
                        rawType.includes("image") ||
                        rawType.startsWith("image/") ||
                        [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
                          lowerName.endsWith(ext)
                        )
                      ) {
                        displayType = "image";
                      } else if (
                        rawType.includes("word") ||
                        [".doc", ".docx"].some((ext) => lowerName.endsWith(ext))
                      ) {
                        displayType = "word";
                      }

                      return (
                        <div key={index} className="document-item">
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link"
                          >
                            <div className="document-thumbnail-wrapper">
                              {displayType === "image" ? (
                                <img
                                  src={fileUrl}
                                  alt={fileName}
                                  className="document-thumbnail-image"
                                />
                              ) : (
                                <div
                                  className={`document-thumbnail-icon document-thumbnail-icon-${displayType}`}
                                >
                                  {displayType === "pdf" && "PDF"}
                                  {displayType === "word" && "WORD"}
                                  {displayType === "other" && "FILE"}
                                </div>
                              )}
                            </div>
                          </a>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => handleHideDocument(index)}
                              style={{
                                background: "#f59e0b",
                                color: "#fff",
                                border: "none",
                                padding: "4px 10px",
                                borderRadius: "999px",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title="Hide document"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5Z"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <circle
                                  cx="12"
                                  cy="12.5"
                                  r="3"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span>Hide</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveDocument(index)}
                              style={{
                                background: "#ef4444",
                                color: "#fff",
                                border: "none",
                                padding: "4px 10px",
                                borderRadius: "999px",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title="Delete document"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 7H19"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M10 10V16"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M14 10V16"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M6 7L7 18C7 19.1046 7.89543 20 9 20H15C16.1046 20 17 19.1046 17 18L18 7"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M9 7V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p>No documents available</p>
                )}
              </div>
              <div className="form-group">
                <p style={{ fontSize: "0.9em", color: "#666", fontStyle: "italic" }}>Note: You can only delete existing documents. New documents cannot be uploaded from the admin panel.</p>
              </div>
              <hr style={{ margin: "20px 0", border: "1px solid #ddd" }} />
              <h4 style={{ marginBottom: "15px" }}>Secondary Profession</h4>
              <div className="form-group">
                <label htmlFor="secondaryProfession">Secondary Profession</label>
                <select
                  id="secondaryProfession"
                  name="secondaryProfession"
                  value={formData.secondaryProfession || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select a profession (optional)</option>
                  {professionOptions.map((profession) => (
                    <option key={profession.value} value={profession.value}>
                      {profession.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="secondaryDescription">Secondary Description</label>
                <textarea
                  id="secondaryDescription"
                  name="secondaryDescription"
                  value={formData.secondaryDescription || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Secondary Services</label>
                {formData.secondaryProfession ? (
                  <div className="services-checkboxes" ref={servicesContainerRef} style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {getServicesForProfession(formData.secondaryProfession).map((service) => {
                      const serviceValue = service.value || service.label || service;
                      const serviceLabel = service.label || service.value || service;
                      const hasSubServices = service.subServices && service.subServices.length > 0;
                      const subServices = hasSubServices ? getSubServicesForService(formData.secondaryProfession, serviceValue) : [];
                      const isServiceSelected = (formData.secondaryServices || []).includes(serviceValue) ||
                        (formData.secondaryServices || []).includes(serviceLabel);

                      return (
                        <div key={serviceValue} className="checkbox-item" style={{ marginBottom: hasSubServices ? "15px" : "5px" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <input
                              type="checkbox"
                              id={`secondary-service-${serviceValue}`}
                              value={serviceValue}
                              checked={isServiceSelected}
                              onChange={() => {
                                const current = formData.secondaryServices || [];
                                const updated = isServiceSelected
                                  ? current.filter(s => s !== serviceValue && s !== serviceLabel)
                                  : [...current, serviceValue];
                                setFormData({ ...formData, secondaryServices: updated });
                              }}
                            />
                            <label htmlFor={`secondary-service-${serviceValue}`} style={{ marginLeft: "8px", fontWeight: hasSubServices ? "bold" : "normal" }}>
                              {serviceLabel}
                            </label>
                          </div>
                          {hasSubServices && isServiceSelected && (
                            <div style={{ marginLeft: "25px", marginTop: "8px", paddingLeft: "10px", borderLeft: "2px solid #3b82f6" }}>
                              <label style={{ fontSize: "0.9em", color: "#666", marginBottom: "5px", display: "block" }}>Sub Services:</label>
                              {subServices.map((subService) => {
                                const subValue = subService.value || subService;
                                const subLabel = subService.label || subService.value || subService;
                                const isSubSelected = (formData.secondarySubServices || []).includes(subValue) ||
                                  (formData.secondarySubServices || []).includes(subLabel);
                                return (
                                  <div key={subValue} style={{ marginBottom: "5px" }}>
                                    <input
                                      type="checkbox"
                                      id={`secondary-subservice-${serviceValue}-${subValue}`}
                                      checked={isSubSelected}
                                      onChange={() => {
                                        const currentSubServices = formData.secondarySubServices || [];
                                        const newSubServices = isSubSelected
                                          ? currentSubServices.filter(s => s !== subValue && s !== subLabel)
                                          : [...currentSubServices, subValue];
                                        setFormData({ ...formData, secondarySubServices: newSubServices });
                                      }}
                                    />
                                    <label htmlFor={`secondary-subservice-${serviceValue}-${subValue}`} style={{ marginLeft: "8px", fontSize: "0.85em" }}>
                                      {subLabel}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: "#999", fontStyle: "italic" }}>Please select a secondary profession first</p>
                )}
              </div>
              {hasVehicleFields(formData.secondaryProfession) && (() => {
                const vehicleFields = getVehicleFields(formData.secondaryProfession);
                if (!vehicleFields) return null;
                return (
                  <>
                    {vehicleFields.brandExpertise.length > 0 && (
                      <div className="form-group">
                        <label>Secondary Brand Expertise</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.brandExpertise.map((brand) => {
                            const isSelected = (formData.secondaryBrandExpertise || []).includes(brand);
                            return (
                              <div key={brand} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`secondary-brand-${brand}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.secondaryBrandExpertise || [];
                                    const updated = isSelected
                                      ? current.filter(b => b !== brand)
                                      : [...current, brand];
                                    setFormData({ ...formData, secondaryBrandExpertise: updated });
                                  }}
                                />
                                <label htmlFor={`secondary-brand-${brand}`} style={{ marginLeft: "8px" }}>{brand}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {vehicleFields.engineCC.length > 0 && (
                      <div className="form-group">
                        <label>Secondary Engine CC</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.engineCC.map((cc) => {
                            const isSelected = (formData.secondaryEngineCC || []).includes(cc);
                            return (
                              <div key={cc} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`secondary-engineCC-${cc}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.secondaryEngineCC || [];
                                    const updated = isSelected
                                      ? current.filter(c => c !== cc)
                                      : [...current, cc];
                                    setFormData({ ...formData, secondaryEngineCC: updated });
                                  }}
                                />
                                <label htmlFor={`secondary-engineCC-${cc}`} style={{ marginLeft: "8px" }}>{cc}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {vehicleFields.fuelType.length > 0 && (
                      <div className="form-group">
                        <label>Secondary Fuel Type</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.fuelType.map((fuel) => {
                            const isSelected = (formData.secondaryFuelType || []).includes(fuel);
                            return (
                              <div key={fuel} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`secondary-fuel-${fuel}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.secondaryFuelType || [];
                                    const updated = isSelected
                                      ? current.filter(f => f !== fuel)
                                      : [...current, fuel];
                                    setFormData({ ...formData, secondaryFuelType: updated });
                                  }}
                                />
                                <label htmlFor={`secondary-fuel-${fuel}`} style={{ marginLeft: "8px" }}>{fuel}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {vehicleFields.workshopType.length > 0 && (
                      <div className="form-group">
                        <label>Secondary Workshop Type</label>
                        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ddd", padding: "10px", borderRadius: "4px" }}>
                          {vehicleFields.workshopType.map((workshop) => {
                            const isSelected = (formData.secondaryWorkshopType || []).includes(workshop);
                            return (
                              <div key={workshop} style={{ marginBottom: "5px" }}>
                                <input
                                  type="checkbox"
                                  id={`secondary-workshop-${workshop}`}
                                  checked={isSelected}
                                  onChange={() => {
                                    const current = formData.secondaryWorkshopType || [];
                                    const updated = isSelected
                                      ? current.filter(w => w !== workshop)
                                      : [...current, workshop];
                                    setFormData({ ...formData, secondaryWorkshopType: updated });
                                  }}
                                />
                                <label htmlFor={`secondary-workshop-${workshop}`} style={{ marginLeft: "8px" }}>{workshop}</label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="form-group">
                <label>Current Secondary Media</label>
                {secondaryMedia.length > 0 || newSecondaryMediaFiles.length > 0 ? (
                  <div className="documents-list">
                    {secondaryMedia.map((item, index) => (
                      <div key={index} className="document-item">
                        <div
                          className="document-link"
                          onClick={() => handleMediaClick(item, index, false, 'secondary')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="document-thumbnail-wrapper">
                            {item.type === "image" ? (
                              <img
                                src={item.url}
                                alt={`Secondary Media ${index + 1}`}
                                className="document-thumbnail-image"
                                onError={(e) => {
                                  e.target.src = DEFAULT_IMAGE;
                                }}
                              />
                            ) : (
                              <video
                                src={item.url}
                                controls
                                className="document-thumbnail-image"
                                onError={(e) => {
                                  console.error("Video load error:", e);
                                }}
                              />
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "8px",
                            marginTop: "8px",
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHideSecondaryMedia(index);
                            }}
                            style={{
                              background: "#f59e0b",
                              color: "#fff",
                              border: "none",
                              padding: "4px 10px",
                              borderRadius: "999px",
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title="Hide secondary media"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5Z"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <circle
                                cx="12"
                                cy="12.5"
                                r="3"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span>Hide</span>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSecondaryMedia(index);
                            }}
                            style={{
                              background: "#ef4444",
                              color: "#fff",
                              border: "none",
                              padding: "4px 10px",
                              borderRadius: "999px",
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title="Delete secondary media"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5 7H19"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 10V16"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 10V16"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 7L7 18C7 19.1046 7.89543 20 9 20H15C16.1046 20 17 19.1046 17 18L18 7"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9 7V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-media">No secondary media available</p>
                )}
              </div>
              <div className="form-group">
                <p style={{ fontSize: "0.9em", color: "#666", fontStyle: "italic" }}>Note: You can only delete existing secondary media files. New files cannot be uploaded from the admin panel.</p>
              </div>
              <div className="form-group">
                <label>Current Secondary Documents</label>
                {secondaryDocuments.length > 0 ? (
                  <div className="documents-list">
                    {secondaryDocuments.map((doc, index) => {
                      const fileUrl = doc?.file?.url || doc?.url || "";
                      const fileName =
                        doc?.file?.name || doc?.name || `Secondary Document ${index + 1}`;
                      const rawType = (doc?.file?.type || doc?.type || "unknown").toLowerCase();
                      const lowerName = fileName.toLowerCase();

                      let displayType = "other";
                      if (rawType.includes("pdf") || lowerName.endsWith(".pdf")) {
                        displayType = "pdf";
                      } else if (
                        rawType.includes("image") ||
                        rawType.startsWith("image/") ||
                        [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
                          lowerName.endsWith(ext)
                        )
                      ) {
                        displayType = "image";
                      } else if (
                        rawType.includes("word") ||
                        [".doc", ".docx"].some((ext) => lowerName.endsWith(ext))
                      ) {
                        displayType = "word";
                      }

                      return (
                        <div key={index} className="document-item">
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link"
                          >
                            <div className="document-thumbnail-wrapper">
                              {displayType === "image" ? (
                                <img
                                  src={fileUrl}
                                  alt={fileName}
                                  className="document-thumbnail-image"
                                />
                              ) : (
                                <div
                                  className={`document-thumbnail-icon document-thumbnail-icon-${displayType}`}
                                >
                                  {displayType === "pdf" && "PDF"}
                                  {displayType === "word" && "WORD"}
                                  {displayType === "other" && "FILE"}
                                </div>
                              )}
                            </div>
                          </a>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => handleHideSecondaryDocument(index)}
                              style={{
                                background: "#f59e0b",
                                color: "#fff",
                                border: "none",
                                padding: "4px 10px",
                                borderRadius: "999px",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title="Hide secondary document"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5Z"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <circle
                                  cx="12"
                                  cy="12.5"
                                  r="3"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span>Hide</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveSecondaryDocument(index)}
                              style={{
                                background: "#ef4444",
                                color: "#fff",
                                border: "none",
                                padding: "4px 10px",
                                borderRadius: "999px",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title="Delete secondary document"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 7H19"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M10 10V16"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M14 10V16"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M6 7L7 18C7 19.1046 7.89543 20 9 20H15C16.1046 20 17 19.1046 17 18L18 7"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M9 7V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p>No secondary documents available</p>
                )}
              </div>
              <div className="form-group">
                <p style={{ fontSize: "0.9em", color: "#666", fontStyle: "italic" }}>Note: You can only delete existing secondary documents. New documents cannot be uploaded from the admin panel.</p>
              </div>
              <div className="modal-buttons">
                <button type="submit" className="save-btn">
                  Save Changes
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setIsEditing(false);
                    setNewMediaFiles([]);
                    setNewSecondaryMediaFiles([]);
                    setNewDocumentFiles([]);
                    setNewSecondaryDocumentFiles([]);
                    setProfilePictureFile(null);
                    setProfilePicturePreview(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Servicemen Stats Section */}
      <div className="stats-section">
        <ProfileSubsectionWithCorrection
          title="Call Action Records"
          fieldKey="callActionRecords"
          canEdit={canEditServiceman}
          correctionFields={correctionFields}
          toggleField={toggleCorrectionField}
        >
          {loadingStats ? (
            <div className="loading-container">
              <span className="loader"></span>
            </div>
          ) : servicemenStats.length > 0 ? (
            <>
              <div className="stats-table-container">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Type</th>
                      <th>User Name</th>
                      <th>User ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servicemenStats.map((stat, index) => {
                      const timestamp = stat.timestamp?._seconds
                        ? new Date(stat.timestamp._seconds * 1000).toLocaleString()
                        : stat.timestamp?.toMillis
                          ? new Date(stat.timestamp.toMillis()).toLocaleString()
                          : stat.timestamp instanceof Date
                            ? stat.timestamp.toLocaleString()
                            : formatFieldValue(stat.timestamp);

                      return (
                        <tr key={stat.id || index}>
                          <td>{timestamp}</td>
                          <td>
                            <span className={`type-badge type-${stat.type || 'unknown'}`}>
                              {stat.type || 'N/A'}
                            </span>
                          </td>
                          <td className="user-name-cell">{stat.userName || 'Unknown User'}</td>
                          <td className="user-id-cell">{stat.userId || 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="stats-summary">
                <p>Total Records: <strong>{servicemenStats.length}</strong></p>
              </div>
            </>
          ) : (
            <p className="no-stats">No call action records found for this serviceman.</p>
          )}
        </ProfileSubsectionWithCorrection>
      </div>

      <style>{`
        :root {
          --primary-color: #3b82f6;
          --secondary-color: #1f2937;
          --accent-color: #dc2626;
          --bg-color: #f9fafb;
          --text-color: #111827;
          --border-radius: 8px;
          --shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          --transition: all 0.3s ease;
          --primary-bg: #e3f2fd;
          --secondary-bg: #f3e5f5;
        }

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .serviceman-profile {
          width: 100%;
          margin: 0;
          padding: 2rem;
          box-sizing: border-box;
          background: var(--bg-primary, #f9fafb);
          min-height: 100vh;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header h2 {
          font-size: 2rem;
          color: var(--text-primary, #1f2937);
          font-weight: 700;
        }

        .button-group {
          display: flex;
          gap: 1rem;
        }

        button {
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          border: none;
          border-radius: var(--border-radius);
          cursor: pointer;
          transition: var(--transition);
        }

        .edit-btn {
          background: var(--primary-color, #3b82f6);
          color: white;
        }

        .edit-btn:hover {
          background: #2563eb;
        }

        .back-btn {
          background: #6b7280;
          color: white;
        }

        .back-btn:hover {
          background: #4b5563;
        }

        .profile-container {
          background: var(--bg-card, #ffffff);
          padding: 2rem;
          border-radius: var(--border-radius, 8px);
          box-shadow: var(--shadow, 0 4px 6px rgba(0, 0, 0, 0.1));
          margin-bottom: 2rem;
        }

        .profession-section {
          margin-bottom: 2rem;
        }

        .primary-profession {
          border-left: 4px solid var(--primary-color);
        }

        .secondary-profession {
          border-left: 4px solid #9c27b0;
        }

        .section-title {
          font-size: 1.75rem;
          color: var(--text-primary, #1f2937);
          margin-bottom: 1.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 3px solid var(--primary-color, #3b82f6);
          font-weight: 700;
        }

        .secondary-profession .section-title {
          border-bottom-color: #9c27b0;
        }

        .correction-save-btn {
          padding: 0.75rem 1.25rem;
          font-size: 0.95rem;
          font-weight: 600;
          border-radius: var(--border-radius, 8px);
          border: 1px solid #f59e0b;
          background: linear-gradient(180deg, #fef3c7 0%, #fde68a 100%);
          color: #92400e;
          cursor: pointer;
          transition: var(--transition, 0.2s ease);
        }
        .correction-save-btn:hover:not([aria-disabled="true"]) {
          background: linear-gradient(180deg, #fde68a 0%, #fcd34d 100%);
          border-color: #d97706;
        }
        .correction-save-btn[aria-disabled="true"] {
          opacity: 0.45;
          cursor: not-allowed;
        }
        [data-theme="dark"] .correction-save-btn {
          background: linear-gradient(180deg, #78350f 0%, #92400e 100%);
          color: #fef3c7;
          border-color: #f59e0b;
        }
        [data-theme="dark"] .correction-save-btn:hover:not([aria-disabled="true"]) {
          background: linear-gradient(180deg, #92400e 0%, #b45309 100%);
        }

        .correction-save-wrap {
          position: relative;
          display: inline-block;
          align-self: flex-start;
        }
        .correction-preview-panel {
          position: absolute;
          left: 0;
          top: calc(100% + 6px);
          z-index: 50;
          min-width: min(260px, 92vw);
          max-width: min(360px, 96vw);
          max-height: 70vh;
          overflow: auto;
          padding: 0.75rem 0.85rem;
          border-radius: var(--border-radius, 8px);
          border: 1px solid var(--border-color, #e5e7eb);
          background: var(--bg-card, #ffffff);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
        }
        [data-theme="dark"] .correction-preview-panel {
          border-color: #4b5563;
          background: #1f2937;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.45);
        }
        .correction-preview-empty {
          font-size: 0.875rem;
          color: var(--text-secondary, #6b7280);
          margin: 0;
          line-height: 1.45;
        }
        .correction-preview-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }
        .correction-preview-row {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          padding: 0.5rem 0.4rem;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        [data-theme="dark"] .correction-preview-row {
          border-bottom-color: #374151;
        }
        .correction-preview-row:last-child {
          border-bottom: none;
        }
        .correction-preview-row-main {
          flex: 1;
          min-width: 0;
        }
        .correction-preview-key {
          display: block;
          font-weight: 700;
          font-size: 0.8rem;
          color: var(--primary-color, #3b82f6);
          margin-bottom: 0.35rem;
          word-break: break-word;
        }
        .correction-preview-present {
          margin-top: 0.15rem;
        }
        .correction-preview-present[data-field="image"] .correction-rich-media-block {
          align-items: center;
        }
        .correction-preview-present[data-field="image"] .correction-rich-img {
          width: 72px;
          height: 72px;
          max-width: 72px;
          max-height: 72px;
          border-radius: 50%;
          object-fit: cover;
        }
        .correction-preview-value {
          margin: 0;
          font-size: 0.78rem;
          line-height: 1.35;
          white-space: pre-wrap;
          word-break: break-word;
          color: var(--text-primary, #111827);
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        }
        .correction-preview-value--rich {
          font-family: inherit;
          white-space: normal;
        }
        [data-theme="dark"] .correction-preview-value {
          color: #e5e7eb;
        }
        .correction-preview-remove {
          flex-shrink: 0;
          padding: 0.35rem 0.6rem;
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 6px;
          border: 1px solid #dc2626;
          background: #fef2f2;
          color: #991b1b;
          cursor: pointer;
        }
        .correction-preview-remove:hover {
          background: #fee2e2;
        }
        [data-theme="dark"] .correction-preview-remove {
          background: #450a0a;
          color: #fecaca;
          border-color: #f87171;
        }
        .correction-preview-row--staged {
          opacity: 0.92;
        }
        .correction-preview-staged-note {
          margin: 0 0 0.35rem 0;
          font-size: 0.72rem;
          line-height: 1.35;
          color: var(--text-secondary, #6b7280);
        }
        [data-theme="dark"] .correction-preview-staged-note {
          color: #9ca3af;
        }
        .correction-preview-present--muted {
          opacity: 0.55;
          pointer-events: none;
        }
        .correction-preview-undo {
          flex-shrink: 0;
          padding: 0.35rem 0.6rem;
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 6px;
          border: 1px solid var(--primary-color, #3b82f6);
          background: rgba(59, 130, 246, 0.1);
          color: #1d4ed8;
          cursor: pointer;
        }
        .correction-preview-undo:hover {
          background: rgba(59, 130, 246, 0.18);
        }
        [data-theme="dark"] .correction-preview-undo {
          border-color: #60a5fa;
          color: #93c5fd;
          background: rgba(59, 130, 246, 0.12);
        }
        .correction-preview-footer {
          margin-top: 0.65rem;
          padding-top: 0.65rem;
          border-top: 1px solid var(--border-color, #e5e7eb);
        }
        [data-theme="dark"] .correction-preview-footer {
          border-top-color: #374151;
        }
        .correction-preview-apply-btn {
          width: 100%;
          padding: 0.5rem 0.75rem;
          font-size: 0.85rem;
          font-weight: 700;
          border-radius: 8px;
          border: 1px solid #d97706;
          background: linear-gradient(180deg, #fef3c7 0%, #fde68a 100%);
          color: #92400e;
          cursor: pointer;
        }
        .correction-preview-apply-btn:hover:not(:disabled) {
          background: linear-gradient(180deg, #fde68a 0%, #fcd34d 100%);
        }
        .correction-preview-apply-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        [data-theme="dark"] .correction-preview-apply-btn {
          background: linear-gradient(180deg, #78350f 0%, #92400e 100%);
          color: #fef3c7;
          border-color: #f59e0b;
        }
        .correction-preview-footer-hint {
          margin: 0.45rem 0 0;
          font-size: 0.68rem;
          color: var(--text-secondary, #6b7280);
          line-height: 1.35;
        }
        [data-theme="dark"] .correction-preview-footer-hint {
          color: #9ca3af;
        }

        .correction-dual-wrap {
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
          width: 100%;
        }
        .correction-dual-wrap--profile-pic {
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: stretch;
          justify-content: center;
          gap: 0.75rem;
          width: fit-content;
          max-width: min(100%, 17.5rem);
          margin-left: auto;
          margin-right: auto;
        }
        .correction-dual-wrap--profile-pic .correction-value-old,
        .correction-dual-wrap--profile-pic .correction-value-new {
          flex: 0 0 auto;
          min-width: 7.75rem;
          max-width: 8.25rem;
          width: 7.75rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          padding: 0.5rem 0.55rem 0.6rem;
          border-radius: 12px;
        }
        .correction-dual-wrap--profile-pic .correction-value-label {
          font-size: 0.62rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          margin-bottom: 0.4rem;
          width: 100%;
          text-align: left;
        }
        .correction-dual-wrap--profile-pic .correction-value-rich,
        .correction-dual-wrap--profile-pic .correction-value-new-body {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          flex: 1;
          min-height: 0;
        }
        .correction-dual-wrap--profile-pic .correction-rich-media-block {
          align-items: center;
          width: 100%;
        }
        .correction-dual-wrap--profile-pic .correction-value-old .correction-rich-img {
          width: 96px;
          height: 96px;
          max-width: 96px;
          max-height: 96px;
          border-radius: 50%;
          object-fit: cover;
          object-position: center;
          border: none;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        }
        .correction-dual-wrap--profile-pic .correction-value-new-body .profile-pic,
        .correction-dual-wrap--profile-pic .correction-value-new-body .profile-pic--bento {
          width: 96px !important;
          height: 96px !important;
          min-width: 96px;
          min-height: 96px;
          border-radius: 50%;
          object-fit: cover;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        }
        .correction-dual-wrap--profile-pic .correction-rich-file-link--below {
          display: block;
          max-width: 100%;
          text-align: center;
          font-size: 0.65rem;
          line-height: 1.25;
          margin-top: 0.4rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 600;
        }
        /* Profile compare: light panels (reference UI) */
        .correction-dual-wrap--profile-pic .correction-value-old {
          background: #fffbeb;
          border: 1px solid #fbbf24;
        }
        .correction-dual-wrap--profile-pic .correction-value-new {
          background: #eff6ff;
          border: 1px solid #60a5fa;
        }
        .correction-dual-wrap--profile-pic .correction-value-old .correction-value-label {
          color: #c2410c;
        }
        .correction-dual-wrap--profile-pic .correction-value-new .correction-value-label {
          color: #1d4ed8;
        }
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-old {
          background: #374151;
          border: 1px solid #f59e0b;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
        }
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-new {
          background: #374151;
          border: 1px solid #3b82f6;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
        }
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-old .correction-value-label {
          color: #fb923c;
        }
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-new .correction-value-label {
          color: #93c5fd;
        }
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-old .correction-rich-img {
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
        }
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-new-body .profile-pic,
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-new-body .profile-pic--bento {
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
        }
        .correction-dual-wrap--profile-pic .correction-value-old .correction-rich-file-link {
          color: #2563eb;
        }
        [data-theme="dark"] .correction-dual-wrap--profile-pic .correction-value-old .correction-rich-file-link {
          color: #93c5fd;
        }
        .correction-value-old,
        .correction-value-new {
          border-radius: 8px;
          padding: 0.45rem 0.55rem;
        }
        .correction-value-old {
          background: rgba(245, 158, 11, 0.12);
          border: 1px solid rgba(245, 158, 11, 0.35);
        }
        .correction-value-new {
          background: rgba(37, 99, 235, 0.08);
          border: 1px solid rgba(37, 99, 235, 0.22);
        }
        [data-theme="dark"] .correction-value-old {
          background: rgba(245, 158, 11, 0.14);
          border-color: rgba(245, 158, 11, 0.4);
        }
        [data-theme="dark"] .correction-value-new {
          background: rgba(59, 130, 246, 0.12);
          border-color: rgba(59, 130, 246, 0.32);
        }
        .correction-value-label {
          display: block;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 0.2rem;
          color: var(--text-secondary, #6b7280);
        }
        .correction-value-old .correction-value-label {
          color: #b45309;
        }
        .correction-value-new .correction-value-label {
          color: #1d4ed8;
        }
        .correction-value-pre {
          margin: 0;
          font-size: 0.78rem;
          line-height: 1.35;
          white-space: pre-wrap;
          word-break: break-word;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          color: var(--text-primary, #111827);
        }
        [data-theme="dark"] .correction-value-pre {
          color: #e5e7eb;
        }
        .correction-value-new-body {
          font-size: inherit;
        }
        .correction-value-rich {
          min-width: 0;
          font-size: 0.85rem;
        }
        .correction-rich-empty {
          color: var(--text-secondary, #6b7280);
        }
        .correction-rich-text {
          white-space: pre-wrap;
          word-break: break-word;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.78rem;
          line-height: 1.35;
        }
        .correction-rich-media-block {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0;
        }
        .correction-rich-img {
          max-width: 100%;
          max-height: 140px;
          width: auto;
          height: auto;
          border-radius: 8px;
          object-fit: contain;
          display: block;
          border: 1px solid var(--border-color, #e5e7eb);
          background: var(--bg-tertiary, #f3f4f6);
        }
        [data-theme="dark"] .correction-rich-img {
          border-color: #4b5563;
          background: #111827;
        }
        .correction-rich-file-link {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--primary-color, #2563eb);
          text-decoration: underline;
          word-break: break-word;
        }
        .correction-rich-file-link:hover {
          color: #1d4ed8;
        }
        .correction-rich-file-link--below {
          margin-top: 0.35rem;
        }
        [data-theme="dark"] .correction-rich-file-link {
          color: #93c5fd;
        }
        [data-theme="dark"] .correction-rich-file-link:hover {
          color: #bfdbfe;
        }
        .correction-rich-list {
          margin: 0.2rem 0 0;
          padding-left: 1.1rem;
          font-size: 0.8rem;
        }
        .correction-rich-list li {
          margin-bottom: 0.35rem;
        }
        .correction-value-pre--nested {
          max-height: 12rem;
          overflow: auto;
          margin-top: 0.25rem;
        }
        .profile-aadhar-inline {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.25rem;
        }

        .profile-bento-wrap {
          overflow: hidden;
        }
        .profile-bento-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 1rem;
        }
        @media (min-width: 900px) {
          .profile-bento-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        .profile-bento-card {
          position: relative;
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 12px;
          padding: 1rem 1rem 1rem 1rem;
          background: var(--bg-tertiary, #f9fafb);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          min-height: 5.5rem;
          transition: box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .profile-bento-card--wide {
          grid-column: span 2;
        }
        @media (max-width: 640px) {
          .profile-bento-card--wide {
            grid-column: span 1;
          }
        }
        .profile-bento-card--media {
          min-height: auto;
        }
        .profile-bento-card.is-marked {
          border-color: #f59e0b;
          box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.25);
        }
        [data-theme="dark"] .profile-bento-card {
          background: var(--bg-secondary, #1f2937);
          border-color: var(--border-color, #374151);
        }
        [data-theme="dark"] .profile-bento-card.is-marked {
          border-color: #fbbf24;
          box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.2);
        }
        .profile-bento-card-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .profile-bento-label {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-secondary, #6b7280);
        }
        .profile-bento-value {
          font-size: 1rem;
          font-weight: 500;
          color: var(--text-primary, #111827);
          word-break: break-word;
        }
        .profile-bento-value--multiline {
          white-space: pre-wrap;
          line-height: 1.45;
        }
        .profile-bento-value--row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .profile-bento-value--mono {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.85rem;
        }
        .profile-bento-value--media {
          display: flex;
          justify-content: center;
          padding-top: 0.25rem;
        }
        .profile-pic--bento {
          width: 96px;
          height: 96px;
        }
        .profile-bento-link {
          color: var(--primary-color, #3b82f6);
          text-decoration: underline;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .profile-correction-toggle {
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          border-radius: 8px;
          border: 2px solid var(--border-color, #d1d5db);
          background: var(--bg-card, #fff);
          color: transparent;
          font-size: 14px;
          font-weight: 800;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .profile-correction-toggle--active {
          background: #22c55e;
          border-color: #16a34a;
          color: #fff;
        }
        .profile-correction-toggle:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        [data-theme="dark"] .profile-correction-toggle {
          background: var(--bg-primary, #111827);
          border-color: #4b5563;
        }
        [data-theme="dark"] .profile-correction-toggle--active {
          background: #15803d;
          border-color: #22c55e;
          color: #fff;
        }

        .subsection-title {
          font-size: 1.25rem;
          color: var(--text-primary, #1f2937);
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .profile-list {
          list-style: none;
          display: grid;
          gap: 1.5rem;
        }

        .profile-item {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          padding: 1rem;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .profile-item:last-child {
          border-bottom: none;
        }

        .label {
          font-weight: 600;
          color: var(--text-primary, #1f2937);
          width: 150px;
          flex-shrink: 0;
        }

        .value {
          flex: 1;
          color: var(--text-primary, #111827);
          font-size: 1rem;
        }

        .value.description {
          white-space: pre-wrap;
          line-height: 1.5;
        }

        .profile-pic {
          width: 120px;
          height: 120px;
          object-fit: cover;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .services-list {
          list-style: none;
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .services-list li {
          background: var(--bg-tertiary, #e5e7eb);
          padding: 0.5rem 1rem;
          border-radius: var(--border-radius, 8px);
          font-size: 0.9rem;
          color: var(--text-primary, #111827);
        }

        .vehicle-type-card {
          background: var(--bg-tertiary, #f9fafb);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--border-radius, 8px);
          padding: 1.5rem;
          margin-bottom: 1rem;
        }

        .vehicle-type-card:last-child {
          margin-bottom: 0;
        }

        .vehicle-type-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--text-primary, #1f2937);
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid var(--primary-color, #3b82f6);
        }

        .vehicle-type-field {
          margin-bottom: 1rem;
        }

        .vehicle-type-field:last-child {
          margin-bottom: 0;
        }

        .vehicle-type-field strong {
          display: block;
          color: var(--text-primary, #1f2937);
          margin-bottom: 0.5rem;
          font-size: 0.95rem;
        }

        .media-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 1rem;
        }

        .edit-media-grid {
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 0.75rem;
        }

        .media-item-container {
          position: relative;
          overflow: hidden;
          border-radius: var(--border-radius);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .edit-media-grid .media-item-container {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 0.5rem;
        }

        .media-item {
          width: 100%;
          height: 180px;
          object-fit: cover;
          display: block;
          transition: transform 0.3s ease;
        }

        .edit-media-grid .media-item {
          height: 120px;
        }

        .media-item:hover {
          transform: scale(1.05);
        }

        .clickable-media {
          cursor: pointer;
        }

        .profile-picture-fullscreen-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 1);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
        }

        .profile-picture-fullscreen-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .profile-picture-fullscreen-image {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
        }

        .close-profile-picture-fullscreen {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: none;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          min-width: 50px;
          font-size: 30px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
          z-index: 2001;
        }

        .close-profile-picture-fullscreen:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        @media (max-width: 768px) {
          .close-profile-picture-fullscreen {
            top: 10px;
            right: 10px;
            width: 40px;
            height: 40px;
            font-size: 24px;
          }
        }

        .media-viewer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: var(--overlay-dark, rgba(0, 0, 0, 0.95));
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
          padding: 2rem;
        }

        .media-viewer-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .fullscreen-media {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 8px;
        }

        .close-media-viewer {
          position: absolute;
          top: -40px;
          right: 0;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          min-width: 40px;
          min-height: 40px;
          padding: 0;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s ease;
          z-index: 2001;
          box-sizing: border-box;
        }

        .close-media-viewer:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .media-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: none;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          font-size: 2rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s ease;
          z-index: 2001;
        }

        .media-nav-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .prev-btn {
          left: -60px;
        }

        .next-btn {
          right: -60px;
        }

        .media-viewer-info {
          position: absolute;
          bottom: -40px;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          font-size: 1rem;
          background: rgba(0, 0, 0, 0.5);
          padding: 0.5rem 1rem;
          border-radius: 20px;
        }

        @media (max-width: 768px) {
          .media-nav-btn {
            width: 40px;
            height: 40px;
            font-size: 1.5rem;
          }

          .prev-btn {
            left: 10px;
          }

          .next-btn {
            right: 10px;
          }

          .close-media-viewer {
            top: 10px;
            right: 10px;
          }

          .media-viewer-info {
            bottom: 10px;
          }
        }

        .remove-media-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: var(--accent-color);
          color: white;
          border: none;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          font-size: 1.2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: var(--transition);
          z-index: 10;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .edit-media-grid .hide-media-btn,
        .edit-media-grid .remove-media-btn {
          position: static;
          width: auto;
          height: auto;
          border-radius: 999px;
          padding: 4px 10px;
          font-size: 0.75rem;
          box-shadow: none;
          margin: 0;
        }

        .edit-media-grid .hide-media-btn {
          background: #f59e0b;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .edit-media-grid .remove-media-btn {
          background: #ef4444;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .edit-media-grid .hide-media-btn:hover {
          background: #f59e0b;
          transform: none;
        }

        .edit-media-grid .remove-media-btn:hover {
          background: #dc2626;
          transform: none;
        }

        .no-media {
          color: var(--text-muted, #6b7280);
          font-style: italic;
        }

        .documents-list {
          list-style: none;
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .document-item {
          background: var(--bg-tertiary, #f3f4f6);
          border-radius: var(--border-radius, 8px);
          padding: 0;
          transition: var(--transition, all 0.3s ease);
          width: 180px;
          box-sizing: border-box;
        }

        .document-item:hover {
          background: var(--bg-hover, #e5e7eb);
        }

        .document-link {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          text-decoration: none;
          color: var(--text-primary, #111827);
          font-size: 0.9rem;
          width: 100%;
        }

        .document-link:hover {
          color: var(--primary-color, #3b82f6);
        }

        .document-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .document-thumbnail-wrapper {
          width: 100%;
          height: 150px;
          border-radius: var(--border-radius, 8px);
          overflow: hidden;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #11182710;
          border: 1px solid #e5e7eb;
        }

        .document-thumbnail-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .document-thumbnail-icon {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          color: #ffffff;
        }

        .document-thumbnail-icon-pdf {
          background: #ef4444;
        }

        .document-thumbnail-icon-word {
          background: #2563eb;
        }

        .document-thumbnail-icon-other {
          background: #6b7280;
        }

        .document-title {
          flex: 1;
          font-weight: 500;
        }

        .document-type {
          font-size: 0.85rem;
          color: var(--text-muted, #6b7280);
          font-weight: 400;
        }

        .modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: var(--overlay, rgba(0, 0, 0, 0.5));
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: var(--bg-card, #ffffff);
          padding: 2rem;
          border-radius: var(--border-radius, 8px);
          /* Increase modal width for better readability on large screens */
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: var(--shadow, 0 4px 6px rgba(0, 0, 0, 0.1));
        }

        .modal-content h3 {
          font-size: 1.5rem;
          color: var(--text-primary, #1f2937);
          margin-bottom: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          color: var(--text-primary, #1f2937);
          margin-bottom: 0.5rem;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: var(--border-radius, 8px);
          font-size: 1rem;
          color: var(--text-primary, #111827);
          transition: border-color 0.3s ease;
          background: var(--bg-input, #ffffff);
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary-color, #3b82f6);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-group textarea {
          min-height: 100px;
          resize: vertical;
        }

        .services-checkboxes {
          max-height: 200px;
          overflow-y: auto;
          padding: 0.5rem;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: var(--border-radius, 8px);
          background: var(--bg-tertiary, #f9fafb);
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .checkbox-item input {
          width: auto;
        }

        .modal-buttons {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .save-btn {
          background: var(--primary-color);
          color: white;
        }

        .save-btn:hover {
          background: #2563eb;
        }

        .cancel-btn {
          background: #6b7280;
          color: white;
        }

        .cancel-btn:hover {
          background: #4b5563;
        }

        .error {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          color: var(--accent-color);
          text-align: center;
          padding: 1rem;
        }

        .error p {
          font-size: 1.25rem;
          margin-bottom: 1rem;
        }

        @media (max-width: 768px) {
          .header h2 {
            font-size: 1.5rem;
          }

          .profile-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .label {
            width: auto;
          }

          .profile-pic {
            width: 80px;
            height: 80px;
          }

          .media-grid {
            grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          }

          .edit-media-grid {
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          }

          .media-item {
            height: 140px;
          }

          .edit-media-grid .media-item {
            height: 100px;
          }

          .modal-content {
            padding: 1.5rem;
          }

          .vehicle-type-card {
            padding: 1rem;
          }
        }

        @media (max-width: 480px) {
          .button-group {
            flex-direction: column;
            width: 100%;
          }

          button {
            width: 100%;
          }

          .media-grid,
          .edit-media-grid {
            grid-template-columns: 1fr;
          }
        }

        .stats-section {
          margin-top: 2rem;
          margin-bottom: 2rem;
        }

        .stats-table-container {
          overflow-x: auto;
          margin-top: 1rem;
        }

        .stats-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card, #ffffff);
          border-radius: var(--border-radius, 8px);
          overflow: hidden;
          box-shadow: 0 2px 4px var(--shadow, rgba(0, 0, 0, 0.1));
        }

        .stats-table thead {
          background: var(--primary-color, #3b82f6);
          color: white;
        }

        .stats-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .stats-table tbody tr {
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          transition: background-color 0.2s ease;
          background: var(--bg-card, #ffffff);
        }

        [data-theme="dark"] .stats-table tbody tr {
          background: var(--bg-secondary, #2d2d2d);
        }

        .stats-table tbody tr:hover {
          background-color: var(--bg-hover, #f9fafb);
        }

        [data-theme="dark"] .stats-table tbody tr:hover {
          background-color: var(--bg-hover, #353535);
        }

        .stats-table tbody tr:last-child {
          border-bottom: none;
        }

        .stats-table td {
          padding: 1rem;
          color: var(--text-primary, #111827);
          font-size: 0.95rem;
          background: inherit;
        }

        [data-theme="dark"] .stats-table td {
          color: var(--text-primary, #e5e5e5) !important;
        }

        [data-theme="dark"] .stats-table tbody td:first-child {
          color: var(--text-primary, #e5e5e5) !important;
        }

        .type-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .type-call_action {
          background: #dbeafe;
          color: #1e40af;
        }

        [data-theme="dark"] .type-call_action {
          background: #1e3a8a;
          color: #93c5fd;
        }

        .type-unknown {
          background: var(--bg-tertiary, #f3f4f6);
          color: var(--text-muted, #6b7280);
        }

        .user-name-cell {
          font-weight: 500;
          color: var(--text-primary, #111827);
        }

        [data-theme="dark"] .user-name-cell {
          color: var(--text-primary, #e5e5e5) !important;
        }

        .user-id-cell {
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          color: var(--text-muted, #6b7280);
          word-break: break-all;
        }

        [data-theme="dark"] .user-id-cell {
          color: var(--text-secondary, #b0b0b0) !important;
        }

        .stats-summary {
          margin-top: 1.5rem;
          padding: 1rem;
          background: var(--bg-tertiary, #f9fafb);
          border-radius: var(--border-radius, 8px);
          text-align: center;
        }

        .stats-summary p {
          margin: 0;
          color: var(--text-primary, #111827);
          font-size: 1rem;
        }

        .stats-summary strong {
          color: var(--primary-color, #3b82f6);
          font-size: 1.1rem;
        }

        .loading-container {
          padding: 2rem;
          text-align: center;
          color: var(--text-primary, #111827);
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .loading-container .loader {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: inline-block;
          position: relative;
          border: 2px solid #FF3D00;
          box-sizing: border-box;
          animation: rotation 1s linear infinite;
        }
        .loading-container .loader::after {
          content: '';  
          box-sizing: border-box;
          position: absolute;
          left: 4px;
          top: 4px;
          border: 2px solid #000;
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        [data-theme="dark"] .loading-container .loader::after {
          border: 2px solid #FFF;
        }
        @keyframes rotation {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .no-stats {
          padding: 2rem;
          text-align: center;
          color: var(--text-muted, #6b7280);
          font-style: italic;
        }

        @media (max-width: 768px) {
          .stats-table {
            font-size: 0.85rem;
          }

          .stats-table th,
          .stats-table td {
            padding: 0.75rem 0.5rem;
          }

          .user-id-cell,
          .serviceman-id-cell {
            font-size: 0.75rem;
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      `}</style>
    </div>
    </ServicemanCorrectionDisplayContext.Provider>
  );
};

export default ServicemanProfile;
